import {
	HttpClient,
	HttpErrorResponse,
	HttpParams,
} from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, lastValueFrom, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DataResponse } from 'src/app/models/api/dataResponse';
import {
	EditCompanyRequestBody,
	UpdateCompanyRelationshipDetailsRequestBody,
} from 'src/app/models/api/requests';
import { LocaleService } from './locale.service';
import { AccessCodeListResponse } from 'src/app/models/data/accessCode';
import { Company } from 'src/app/models/data/company';
import {
	Device,
	DeviceAccessSource,
	DeviceGroup,
	DeviceGroupDeviceStatus,
	DeviceInstallationPhoto,
	DeviceNote,
	DeviceNoteResponse,
	DeviceSettings,
	DeviceSettingsResponse,
	DeviceEditResponse,
	SerialNumber,
	InstallationLocationResponse,
} from 'src/app/models/data/device';
import {
	AssignableRelationshipType,
	ControlAssignment,
	ControlAssignmentOption,
	DeviceControl,
} from 'src/app/models/data/deviceControl';
import {
	CompanyEntityRelationship,
	CreateCompanyEntityRelationshipRequestBody,
	CreateCompanyRequestBody,
	EntityRelationship,
} from 'src/app/models/data/entityRelationship';
import {
	FlexCodeGen4,
	FlexCodeListResponse,
} from 'src/app/models/data/flexCode';
import {
	OneDayCodeGen3,
	OneDayCodeListResponse,
	OneDayCodeResponse,
} from 'src/app/models/data/oneDayCode';
import { Role, RoleDataItem } from 'src/app/models/data/role';
import {
	SystemCapability,
	SystemCapabilityItem,
} from 'src/app/models/data/systemCapability';
import {
	CompanyWhitelist,
	MfaMethod,
	PhoneNumber,
	SaveNoteRequestBody,
	SaveStatusRequestBody,
	UpdateUserProfile,
	User,
	UserSecurityAnswer,
	UserWithOptionalErrorMessage,
	UserWithRoleName,
	WhitelistStatus,
} from 'src/app/models/data/user';
import {
	AccessReportExportStart,
	AccessReportExportStatus,
	AccessReportRecord,
	KeyDetectionReportRecord,
	CodeGenReport,
	CreatePropertyRequestBody,
	DashboardSettings,
	EditPropertyRequestBody,
	EditDeviceRequestBody,
	EventHistory,
	MapData,
	PropertyReportRecord,
	UpdateUserNotificationPreferencesRequestBody,
	UsageReport,
	UserNotificationPreferencesResponse,
	UserProfile,
	CountryAvailableSms,
} from 'src/app/models/model';
import {
	DeviceDetailResponse,
	EventHistoryResponse,
	NewUserResponse,
	UsersResponse,
	EditCompanyResponse,
	CreateCompanyResponse,
} from 'src/app/models/response';
import { ENV } from 'src/environment.provider';
import { Environment } from 'src/environments/environment';
import { AppVisit, AppVisitNote, AppVisitPhoto } from '../models/data/appVisit';
import { Timezone } from '../models/timezone';
import { AlertReportFilterOptions } from '../ui/reports/alert-report-dialog/alert-report-dialog.component';
import { AlertReport } from '../ui/reports/alert-report-table/alert-report-table.component';
import { LoadingIndicatorService } from './loading-indicator.service';
import Property = Components.Schemas.Location;
import Client = Components.Schemas.Client;
import AddEditDeleteContactDetailResponse = Components.Schemas.AddEditDeleteContactDetailResponse;
import Contacts = Components.Schemas.Contacts;
import AddEditContactDetailRequest = Components.Schemas.AddEditContactDetailRequest;
import Contract = Components.Schemas.Contract;
import AddOnBillingSettings = Components.Schemas.AddOnBillingSettings;
import ContractSettingsFields = Components.Schemas.ContractSettingsFields;
import UserAuthentication = Components.Schemas.UserAuthentication;
import TokenValidity = Components.Schemas.TokenValidity;
import NumericToken = Components.Schemas.NumericToken;
import AccessReportFilterOptions = Components.Schemas.AccessReportFilterOptions;
import KeyDetectionReportFilterOptions = Components.Schemas.KeyDetectionReportFilterOptions;
import UsageReportFilterOptions = Components.Schemas.UsageReportFilterOptions;
import AuditReportRecord = Components.Schemas.AuditReportRecord;
import PropertyReportFilterOptions = Components.Schemas.PropertyReportFilterOptions;
import CodeGenReportFilterOptions = Components.Schemas.CodeGenReportFilterOptions;
import InventoryReportRecord = Components.Schemas.InventoryReportRecord;
import InventoryReportFilterOptions = Components.Schemas.InventoryReportFilterOptions;
import DeviceAlert = Components.Schemas.DeviceAlert;
import DeviceAlertTotal = Components.Schemas.DeviceAlertTotal;
import FeatureFlag = Components.Schemas.FeatureFlag;
import CompanySetting = Components.Schemas.CompanySetting;
import GrantedSystemCapabilities = Components.Schemas.GrantedSystemCapabilities;
import CompanyUserDeviceGroupDeviceCount = Components.Schemas.CompanyUserDeviceGroupDeviceCount;
import UpdateDeviceGroupDevicesWithDeviceLimit = Components.Schemas.UpdateDeviceGroupDevicesWithDeviceLimit;
import CreateDeviceGroupWithDeviceLimit = Components.Schemas.CreateDeviceGroupWithDeviceLimit;
import DevicesResponse = Components.Responses.DevicesResponse;
import { HaspCodeFields } from '../models/data/haspCode';
import { TransferDeviceOwnershipError } from '../models/data/deviceTransfer';
import { DeviceSettingsKV } from '../models/data/deviceSettings';
import AddClientWithNewPropertyRequestBody = Components.RequestBodies.AddClientWithNewPropertyRequestBody;
import AddClientWithNewPropertySuccessResponse = Components.Responses.AddClientWithNewPropertySuccessResponse;
import AddClientWithNewPropertyBody = Components.Schemas.AddClientWithNewPropertyBody;
import CompanyDetailBasedTaxId = Components.Schemas.CompanyWithTaxIdInfo;
import { AnalyticsTrackingService } from './analytics-tracking.service';

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	private static sentrilockCompanyId?: string;
	static router: Router;
	private readonly analytics = inject(AnalyticsTrackingService);

	constructor(
		private readonly httpClient: HttpClient,
		private readonly router: Router,
		private readonly loadingService: LoadingIndicatorService,
		private readonly localeService: LocaleService,
		@Inject(ENV) private env: Environment
	) {
		ApiService.router = this.router;
	}

	private static handleError(response: HttpErrorResponse): Observable<never> {
		if (response.status === 401) {
			ApiService.router?.navigateByUrl('/login');
		}
		if (response.error !== undefined) {
			return throwError(() => response.error);
		} else {
			return throwError(
				() =>
					({
						errorStatusCode: response.status,
						errors: [
							{
								statusCode: response.status,
								title: 'Unexpected error',
								detail: 'An unexpected error occurred',
							},
						],
					}) as APIError
			);
		}
	}

	/**
	 * RFC 3986 encodeURIComponent that also escapes `!'()*` (left literal by
	 * encodeURIComponent). Required so OpenAPI query validation accepts emails
	 * and other filter values containing those characters (DE5719 / KEY-1202).
	 */
	private strictEncodeURIComponent(value: string | number | boolean): string {
		return encodeURIComponent(value).replace(
			/[!'()*]/g,
			(c) => '%' + c.charCodeAt(0).toString(16).toUpperCase()
		);
	}

	private generateHttpParams(queryParams: QueryParam[] = []): HttpParams {
		const params: {
			[param: string]:
				string | number | boolean | ReadonlyArray<string | number | boolean>;
		} = {};
		queryParams.forEach(
			(p) =>
				(params[p.param] = Array.isArray(p.value)
					? p.value.map((val) => this.strictEncodeURIComponent(val))
					: this.strictEncodeURIComponent(p.value))
		);
		const httpParams = new HttpParams();
		return httpParams.appendAll(params);
	}

	async login(
		email: string,
		password: string,
		sessionKeepActiveTimeSeconds?: number
	): Promise<UserAuthentication> {
		this.loadingService.start(ApiLoadingKeys.login);
		const body = { email, password, sessionKeepActiveTimeSeconds };

		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<UserAuthentication>>(
					`${this.env.apiUrl}/api/v1/user/authenticate`,
					body
				)
				.pipe(
					map((it) => {
						if (it.data.loginToken.length > 0) {
							this.analytics.logEvent('login', {
								method: 'username',
							});
						}
						return it.data;
					}),
					catchError((err) => {
						// eslint-disable-next-line no-console
						console.log(err);
						this.loadingService.stop(ApiLoadingKeys.login);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.login);
		return resp!;
	}

	async requestMfaToken(method: MfaMethod): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.requestMfaToken);
		const url = `${this.env.apiUrl}/api/v1/user/authenticate/mfa/request?method=${method}`;
		await lastValueFrom(
			this.httpClient.post(url, {}).pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.requestMfaToken);
					return ApiService.handleError(err);
				})
			)
		);
		this.loadingService.stop(ApiLoadingKeys.requestMfaToken);
	}

	async verifyMfaToken(token: string): Promise<UserAuthentication> {
		this.loadingService.start(ApiLoadingKeys.verifyMfaToken);
		const url = `${this.env.apiUrl}/api/v1/user/authenticate/mfa/validate?token=${token}`;
		const resp = await lastValueFrom(
			this.httpClient.post<DataResponse<UserAuthentication>>(url, {}).pipe(
				map((it) => it.data),
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.verifyMfaToken);
					return ApiService.handleError(err);
				})
			)
		);

		this.loadingService.stop(ApiLoadingKeys.verifyMfaToken);
		return resp!;
	}

	async updateCustomClaims(
		activeEntityRelationshipId: string
	): Promise<UserAuthentication> {
		this.loadingService.start(ApiLoadingKeys.updateCustomClaims);
		const body = { activeEntityRelationshipId };
		const resp = await lastValueFrom(
			this.httpClient
				.put<DataResponse<UserAuthentication>>(
					`${this.env.apiUrl}/api/v1/user/authenticate/claims`,
					body
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.updateCustomClaims);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.updateCustomClaims);
		return resp!;
	}

	async getSentrilockCompanyId(): Promise<string> {
		// Sentrilock Company ID won't change, so only get it once and store the value
		if (!ApiService.sentrilockCompanyId) {
			this.loadingService.start(ApiLoadingKeys.getSentrilockCompanyId);
			const url = `${this.env.apiUrl}/api/v1/company/sentrilockCompanyId`;
			const response = await lastValueFrom(
				this.httpClient.get<DataResponse<string>>(url).pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getSentrilockCompanyId);
						return ApiService.handleError(err);
					})
				)
			);
			this.loadingService.stop(ApiLoadingKeys.getSentrilockCompanyId);
			ApiService.sentrilockCompanyId = response;
		}
		return ApiService.sentrilockCompanyId!;
	}

	async sendPasswordResetEmail(email: string): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.sendPasswordResetEmail);
		const url = `${this.env.apiUrl}/api/v1/user/password/reset`;
		await lastValueFrom(
			this.httpClient.post(url, { email }, { observe: 'response' }).pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.sendPasswordResetEmail);
					return ApiService.handleError(err);
				})
			)
		);
		this.loadingService.stop(ApiLoadingKeys.sendPasswordResetEmail);
	}

	async checkPasswordResetTokenStatus(
		token: string
	): Promise<PasswordResetTokenStatus> {
		this.loadingService.start(ApiLoadingKeys.checkPasswordResetTokenStatus);
		const url = `${this.env.apiUrl}/api/v1/user/password/reset?token=${token}`;
		const response = await lastValueFrom(
			this.httpClient
				.get<{ data: PasswordResetTokenStatus }>(url, { observe: 'response' })
				.pipe(
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.checkPasswordResetTokenStatus
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.checkPasswordResetTokenStatus);

		const tokenStatus = response?.body?.data as PasswordResetTokenStatus;
		if (tokenStatus !== null) {
			return tokenStatus;
		} else {
			throw new Error('Interval Server Error');
		}
	}

	async resetPassword(password: string, token: string): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.resetPassword);
		const url = `${this.env.apiUrl}/api/v1/user/password/reset`;
		await lastValueFrom(
			this.httpClient
				.put(url, { password, token }, { observe: 'response' })
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.resetPassword);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.resetPassword);
	}

	async changePassword(
		oldPassword: string,
		newPassword: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.changePassword);
		const url = `${this.env.apiUrl}/api/v1/user/password`;
		await lastValueFrom(
			this.httpClient
				.put(url, { oldPassword, newPassword }, { observe: 'response' })
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.changePassword);
						return ApiService.handleError(err);
					})
				)
		);

		this.loadingService.stop(ApiLoadingKeys.changePassword);
	}

	async getUserProfile(handleError = true): Promise<UserProfile> {
		this.loadingService.start(ApiLoadingKeys.getUserProfile);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<UserProfile>>(
					`${this.env.apiUrl}/api/v1/user/profile`
				)
				.pipe(
					map((it) => it.data),
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.getUserProfile);
						if (handleError) {
							return ApiService.handleError(error);
						}
						return throwError(() => error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getUserProfile);
		return resp!;
	}

	async getUserCompanySystemCapabilities(
		userId: string,
		queryParams?: QueryParam[],
		handleError = true
	): Promise<GrantedSystemCapabilities> {
		const params = this.generateHttpParams(queryParams);

		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<GrantedSystemCapabilities>>(
					`${this.env.apiUrl}/api/v1/user/${userId}/systemCapabilities`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((error) => {
						if (handleError) {
							return ApiService.handleError(error);
						}
						return throwError(() => error);
					})
				)
		);
		return resp;
	}

	async updateUser(
		userId: string,
		user: Partial<User>
	): Promise<UserWithOptionalErrorMessage> {
		this.loadingService.start(ApiLoadingKeys.updateUser);
		user = {
			id: userId,
			firstName: user.firstName,
			lastName: user.lastName,
			email: user.email,
			phoneNumber: user.phoneNumber,
			active: user.active ?? true,
			isWhitelisted: user.isWhitelisted ?? false,
		};

		if (
			user.phoneNumber?.rawPhoneNumber &&
			!user.phoneNumber?.rawPhoneNumber?.startsWith('+')
		) {
			user.phoneNumber.rawPhoneNumber = `+${user.phoneNumber.rawPhoneNumber}`;
		}

		const resp = await lastValueFrom(
			this.httpClient
				.put<DataResponse<UserWithOptionalErrorMessage>>(
					`${this.env.apiUrl}/api/v1/user/${userId}`,
					user
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.updateUser);
						return ApiService.handleError(err);
					})
				)
		);

		this.loadingService.stop(ApiLoadingKeys.updateUser);
		return resp!;
	}

	async updateUserProfile(
		updateUserProfileObject: UpdateUserProfile
	): Promise<UserProfile> {
		this.loadingService.start(ApiLoadingKeys.updateUserProfile);
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<UserProfile>>(
					`${this.env.apiUrl}/api/v1/user/profile`,
					updateUserProfileObject
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.updateUserProfile);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.updateUserProfile);
		return resp!;
	}

	async getUserNotificationPreferences(
		handleError = true
	): Promise<UserNotificationPreferencesResponse> {
		this.loadingService.start(ApiLoadingKeys.getUserNotificationPreferences);
		const resp = await lastValueFrom(
			this.httpClient
				.get<UserNotificationPreferencesResponse>(
					`${this.env.apiUrl}/api/v1/user/notificationPreferences`
				)
				.pipe(
					catchError((error) => {
						this.loadingService.stop(
							ApiLoadingKeys.getUserNotificationPreferences
						);
						if (handleError) {
							return ApiService.handleError(error);
						}
						return throwError(() => error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getUserNotificationPreferences);
		return resp!;
	}

	async updateUserNotificationPreferences(
		userNotificationPreferences: UpdateUserNotificationPreferencesRequestBody
	): Promise<UserNotificationPreferencesResponse> {
		this.loadingService.start(ApiLoadingKeys.updateUserNotificationPreferences);
		const resp = await lastValueFrom(
			this.httpClient
				.put<UserNotificationPreferencesResponse>(
					`${this.env.apiUrl}/api/v1/user/notificationPreferences`,
					userNotificationPreferences
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.updateUserNotificationPreferences
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.updateUserNotificationPreferences);
		return resp!;
	}

	async validateEmail(email: string): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.validateEmail);
		const resp = await lastValueFrom(
			this.httpClient
				.post<{
					data: { valid: boolean };
				}>(`${this.env.apiUrl}/api/v1/user/validateEmail`, { email })
				.pipe(
					map((it) => it.data.valid),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.validateEmail);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.validateEmail);
		return resp!;
	}

	async getUserWhitelistedStatus(userId: string): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.getUserDetails);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<WhitelistStatus>>(
					`${this.env.apiUrl}/api/v1/user/${userId}/whitelistStatus`
				)
				.pipe(
					map((it) => it.data.isWhitelisted),
					catchError((err) => {
						return ApiService.handleError(err);
					})
				)
		);

		this.loadingService.stop(ApiLoadingKeys.getUserDetails);
		return resp!;
	}

	async requestEmailVerificationCode(newEmail: string): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.requestEmailVerificationCode);
		await lastValueFrom(
			this.httpClient
				.post(
					`${this.env.apiUrl}/api/v1/user/profile/email/requestChangeToken`,
					{ newEmail }
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.requestEmailVerificationCode
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.requestEmailVerificationCode);
	}

	async verifyEmailVerificationCode(
		newEmail: string,
		token: NumericToken
	): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.verifyEmailVerificationCode);
		const resp = await lastValueFrom(
			this.httpClient
				.post<{
					data: { valid: boolean };
				}>(
					`${this.env.apiUrl}/api/v1/user/profile/email/verifyChangeToken?token=${token}`,
					{ newEmail }
				)
				.pipe(
					map((it) => it.data.valid),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.verifyEmailVerificationCode
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.verifyEmailVerificationCode);
		return resp!;
	}

	async getUserDetails(uid: string): Promise<User> {
		this.loadingService.start(ApiLoadingKeys.getUserDetails);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<User>>(`${this.env.apiUrl}/api/v1/user/${uid}`)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getUserDetails);
						return ApiService.handleError(err);
					})
				)
		);

		this.loadingService.stop(ApiLoadingKeys.getUserDetails);
		return resp!;
	}

	async getUserAndRole(
		token: string,
		userId: string
	): Promise<{ user: User; roleName: string; roleCompanyName: string }> {
		this.loadingService.start(ApiLoadingKeys.getUserAndRole);
		const resp = await lastValueFrom(
			this.httpClient
				.get<
					DataResponse<{
						user: User;
						roleName: string;
						roleCompanyName: string;
					}>
				>(
					`${this.env.apiUrl}/api/v1/user/${userId}/registration?token=${token}`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getUserAndRole);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getUserAndRole);
		return resp!;
	}

	async getUserEventHistory(
		id: string,
		queryParams?: QueryParam[]
	): Promise<EventHistory[]> {
		this.loadingService.start(ApiLoadingKeys.getUserEventHistory);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<EventHistoryResponse>(
					`${this.env.apiUrl}/api/v1/user/details/${id}/eventHistory`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getUserEventHistory);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getUserEventHistory);
		return resp!;
	}

	async getAllUsers(queryParams?: QueryParam[]): Promise<User[]> {
		this.loadingService.start(ApiLoadingKeys.getAllUsers);
		let params: HttpParams = this.generateHttpParams(queryParams);
		params = params.set('allUsers', 'true');

		const resp = await lastValueFrom(
			this.httpClient
				.get<UsersResponse>(`${this.env.apiUrl}/api/v1/user`, { params })
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getAllUsers);
						return ApiService.handleError(err);
					})
				)
		);

		this.loadingService.stop(ApiLoadingKeys.getAllUsers);
		return resp!;
	}

	async resendUserRegistration(userId: string): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.resendUserRegistration);
		await lastValueFrom(
			this.httpClient
				.post<DataResponse<User>>(
					`${this.env.apiUrl}/api/v1/user/${userId}/registration/renew`,
					{}
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.resendUserRegistration);
						return ApiService.handleError(err);
					})
				)
		);

		this.loadingService.stop(ApiLoadingKeys.resendUserRegistration);
	}

	async searchCompaniesPublicDirectory(
		queryParams?: QueryParam[]
	): Promise<Company[]> {
		this.loadingService.start(ApiLoadingKeys.searchCompaniesPublicDirectory);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<Company[]>>(`${this.env.apiUrl}/api/v1/company`, {
					params,
				})
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.searchCompaniesPublicDirectory
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.searchCompaniesPublicDirectory);
		return resp!;
	}

	async getCompanyById(id: string): Promise<{
		company: Company;
		relationship?: EntityRelationship;
		timezone?: Timezone;
	}> {
		this.loadingService.start(ApiLoadingKeys.getCompanyById);
		const resp = await lastValueFrom(
			this.httpClient
				.get<
					DataResponse<{
						company: Company;
						relationship?: EntityRelationship;
						timezone?: Timezone;
					}>
				>(`${this.env.apiUrl}/api/v1/company/${id}`)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getCompanyById);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getCompanyById);
		return resp!;
	}

	async getCompanyByInfo(queryParams?: any): Promise<Company[]> {
		this.loadingService.start(ApiLoadingKeys.getCompanyByInfo);
		// Encode each param value individually using URL-safe Base64 :
		const params = new HttpParams()
			.set('name', queryParams?.name ?? '')
			.set('taxId', this.encodeParam(queryParams?.taxId ?? ''))
			.set('countryCode', queryParams?.countryCode ?? '');
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<Company[]>>(
					`${this.env.apiUrl}/api/v1/company/exist`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getCompanyByInfo);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getCompanyByInfo);
		return resp!;
	}

	//* Verify Tax Id based on value and return get the associated company or companies :
	async verifyTaxIdBasedOnValue(
		queryParams?: any
	): Promise<CompanyDetailBasedTaxId> {
		this.loadingService.start(
			ApiLoadingKeys.getVerificationOfTaxIdBasedOnValue
		);

		const params = new HttpParams()
			// Encode each param value individually using URL-safe Base64 :
			.set('taxId', this.encodeParam(queryParams?.taxId ?? ''))
			.set('countryCode', queryParams?.countryCode ?? '');

		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<CompanyDetailBasedTaxId>>(
					`${this.env.apiUrl}/api/v1/company/exist/taxId`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getVerificationOfTaxIdBasedOnValue
						);
						return ApiService.handleError(err);
					})
				)
		);

		this.loadingService.stop(ApiLoadingKeys.getVerificationOfTaxIdBasedOnValue);
		return resp!;
	}

	//* Reusable URL-safe Base64 encoder for Special characters or Language special characters in Tax ID values :
	private encodeParam(value: string): string {
		const bytes = new TextEncoder().encode(value);
		const binary = String.fromCharCode(...bytes);
		return btoa(binary)
			.replace(/\+/g, '-')
			.replace(/\//g, '_')
			.replace(/=/g, '');
	}

	async editCompanyById(
		id: string,
		requestBody: EditCompanyRequestBody
	): Promise<EditCompanyResponse> {
		this.loadingService.start(ApiLoadingKeys.editCompanyById);
		const resp = await lastValueFrom(
			this.httpClient
				.put<EditCompanyResponse>(
					`${this.env.apiUrl}/api/v1/company/${id}`,
					requestBody
				)
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.editCompanyById);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.editCompanyById);
		return resp!;
	}

	async updateCompanyRelationship(
		requestBody: UpdateCompanyRelationshipDetailsRequestBody
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.updateCompanyRelationship);
		await lastValueFrom(
			this.httpClient
				.post(`${this.env.apiUrl}/api/v1/company/relationship`, requestBody)
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.updateCompanyRelationship);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.updateCompanyRelationship);
	}

	async getCompanyRelationships(
		queryParams?: QueryParam[]
	): Promise<CompanyEntityRelationship[]> {
		this.loadingService.start(ApiLoadingKeys.getCompanyRelationships);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<CompanyEntityRelationship[]>>(
					`${this.env.apiUrl}/api/v1/relationships`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getCompanyRelationships);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getCompanyRelationships);
		return resp!;
	}

	async getCompanyFilterOptions(
		queryParams?: QueryParam[]
	): Promise<CompanyEntityRelationship[]> {
		this.loadingService.start(ApiLoadingKeys.getCompanyFilterOptions);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<CompanyEntityRelationship[]>>(
					`${this.env.apiUrl}/api/v1/report/companyOptions`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getCompanyFilterOptions);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getCompanyFilterOptions);
		return resp!;
	}

	async getPostalFilterOptions(
		queryParams?: QueryParam[]
	): Promise<CompanyEntityRelationship[]> {
		this.loadingService.start(ApiLoadingKeys.getPostalCodeFilterOptions);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<CompanyEntityRelationship[]>>(
					`${this.env.apiUrl}/api/v1/report/postalCodeOptions`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getPostalCodeFilterOptions);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getPostalCodeFilterOptions);
		return resp!;
	}

	async getDeviceGroupFilterOptions(
		queryParams?: QueryParam[]
	): Promise<DeviceGroup[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceGroupFilterOptions);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<DeviceGroup[]>>(
					`${this.env.apiUrl}/api/v1/report/deviceGroupOptions`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getDeviceGroupFilterOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceGroupFilterOptions);
		return resp!;
	}

	async getInstallationLocationNameFilterOptions(
		queryParams?: QueryParam[]
	): Promise<string[]> {
		this.loadingService.start(
			ApiLoadingKeys.getInstallationLocationNameFilterOptions
		);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<InstallationLocationResponse>>(
					`${this.env.apiUrl}/api/v1/report/installationLocationNameOptions`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getInstallationLocationNameFilterOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(
			ApiLoadingKeys.getInstallationLocationNameFilterOptions
		);
		const installationLocationNamesArray = resp.map(
			(locationObj) => locationObj.installationLocationName
		);
		return installationLocationNamesArray!;
	}

	async getCompanyRelationshipsByCompanyId(
		companyId: string,
		queryParams?: QueryParam[]
	): Promise<CompanyEntityRelationship[]> {
		this.loadingService.start(
			ApiLoadingKeys.getCompanyRelationshipsByCompanyId
		);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<CompanyEntityRelationship[]>>(
					`${this.env.apiUrl}/api/v1/company/${companyId}/relationship`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getCompanyRelationshipsByCompanyId
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getCompanyRelationshipsByCompanyId);
		return resp!;
	}

	async createCompanyRelationship(
		requestBody: CreateCompanyEntityRelationshipRequestBody,
		parentCompanyEntityId: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.createCompanyRelationship);
		await lastValueFrom(
			this.httpClient
				.post(
					`${this.env.apiUrl}/api/v1/company/${parentCompanyEntityId}/relationship`,
					requestBody
				)
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.createCompanyRelationship);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.createCompanyRelationship);
	}

	async createNewCompany(
		requestBody: CreateCompanyRequestBody
	): Promise<CreateCompanyResponse> {
		this.loadingService.start(ApiLoadingKeys.createNewCompany);
		const resp = await lastValueFrom(
			this.httpClient
				.post<CreateCompanyResponse>(
					`${this.env.apiUrl}/api/v1/company/`,
					requestBody
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.createNewCompany);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.createNewCompany);
		return resp!;
	}

	async getCompanyRolesByCompanyId(
		companyId: string,
		queryParams?: QueryParam[]
	): Promise<RoleDataItem[]> {
		this.loadingService.start(ApiLoadingKeys.getCompanyRolesByCompanyId);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<RoleDataItem[]>>(
					`${this.env.apiUrl}/api/v1/company/${companyId}/role`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getCompanyRolesByCompanyId);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getCompanyRolesByCompanyId);
		return resp!;
	}

	async getCompanyOwnsDevices(companyId: string): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.getCompanyOwnsDevices);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<boolean>>(
					`${this.env.apiUrl}/api/v1/company/${companyId}/ownsDevices`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getCompanyOwnsDevices);
		return resp!;
	}

	async createDeviceGroup(
		companyId: string,
		groupName: string,
		deviceIds: string[]
	): Promise<CreateDeviceGroupWithDeviceLimit> {
		this.loadingService.start(ApiLoadingKeys.createDeviceGroup);
		const body: { groupName: string; deviceIds: string[] } = {
			groupName,
			deviceIds,
		};
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<CreateDeviceGroupWithDeviceLimit>>(
					`${this.env.apiUrl}/api/v1/company/${companyId}/deviceGroup`,
					body
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.createDeviceGroup);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.createDeviceGroup);
		return resp!;
	}

	async updateDeviceGroupMembership(
		deviceGroupId: string,
		deviceIds: string[],
		action: 'add' | 'remove'
	): Promise<UpdateDeviceGroupDevicesWithDeviceLimit> {
		this.loadingService.start(ApiLoadingKeys.updateDeviceGroupMembership);
		const resp = await lastValueFrom(
			this.httpClient
				.put<DataResponse<UpdateDeviceGroupDevicesWithDeviceLimit>>(
					`${this.env.apiUrl}/api/v1/deviceGroup/${deviceGroupId}`,
					{ deviceIds, action }
				)
				.pipe(
					map((it) => it.data),
					catchError((error) => {
						this.loadingService.stop(
							ApiLoadingKeys.updateDeviceGroupMembership
						);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.updateDeviceGroupMembership);
		return resp;
	}

	async updateDeviceGroupName(
		deviceGroupId: string,
		deviceGroupName: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.updateDeviceGroupName);
		await lastValueFrom(
			this.httpClient
				.put(`${this.env.apiUrl}/api/v1/deviceGroup/${deviceGroupId}`, {
					deviceGroupName,
				})
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.updateDeviceGroupName);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.updateDeviceGroupName);
	}

	async getSystemCapabilitiesByRoleId(
		roleId: string
	): Promise<SystemCapabilityItem[]> {
		this.loadingService.start(ApiLoadingKeys.getSystemCapabilitiesByRoleId);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<SystemCapabilityItem[]>>(
					`${this.env.apiUrl}/api/v1/role/${roleId}/capability`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getSystemCapabilitiesByRoleId
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getSystemCapabilitiesByRoleId);
		return resp!;
	}

	async getRoleCapabilities(): Promise<SystemCapability[]> {
		this.loadingService.start(ApiLoadingKeys.getRoleCapabilities);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<SystemCapability[]>>(
					`${this.env.apiUrl}/api/v1/role/capability`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getRoleCapabilities);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getRoleCapabilities);
		return resp!;
	}

	async getMyRoles(queryParams?: QueryParam[]): Promise<RoleDataItem[]> {
		this.loadingService.start(ApiLoadingKeys.getMyRoles);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<RoleDataItem[]>>(`${this.env.apiUrl}/api/v1/role`, {
					params,
				})
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getMyRoles);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getMyRoles);
		return resp!;
	}

	async getRoleById(
		roleId: string
	): Promise<{ role: Role; systemCapabilities: SystemCapabilityItem[] }> {
		this.loadingService.start(ApiLoadingKeys.getRoleById);
		const resp = await lastValueFrom(
			this.httpClient
				.get<
					DataResponse<{
						role: Role;
						systemCapabilities: SystemCapabilityItem[];
					}>
				>(`${this.env.apiUrl}/api/v1/role/${roleId}`)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getRoleById);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getRoleById);
		return resp!;
	}

	async updateRole(
		roleId: string,
		roleName: string,
		systemCapabilityItems: SystemCapabilityItem[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.updateRole);
		const systemCapabilityIds = systemCapabilityItems
			.filter((it) => it.granted)
			.map((it) => it.systemCapability.id);
		await lastValueFrom(
			this.httpClient
				.put(`${this.env.apiUrl}/api/v1/role/${roleId}`, {
					roleName,
					systemCapabilityIds,
				})
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.updateRole);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.updateRole);
	}

	async getAssignedRoleUsers(
		roleId: string,
		companyId: string,
		queryParams?: QueryParam[]
	): Promise<User[]> {
		this.loadingService.start(ApiLoadingKeys.getAssignedRoleUsers);
		let params = this.generateHttpParams(queryParams);
		params = params.set('companyId', companyId);

		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<User[]>>(
					`${this.env.apiUrl}/api/v1/role/${roleId}/user`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getAssignedRoleUsers);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAssignedRoleUsers);
		return resp!;
	}

	async createNewRole(
		companyId: string,
		name: string,
		systemCapabilityItems: SystemCapabilityItem[]
	): Promise<Role> {
		this.loadingService.start(ApiLoadingKeys.createNewRole);
		const systemCapabilities = systemCapabilityItems
			.filter((it) => it.granted === true)
			.map((it) => it.systemCapability.id);
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<Role>>(`${this.env.apiUrl}/api/v1/role`, {
					companyId,
					name,
					systemCapabilities,
				})
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.createNewRole);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.createNewRole);
		return resp!;
	}

	async assignRoleToUserId(
		userIds: string[],
		roleId: string,
		companyId: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.assignRoleToUserId);
		const body = { roleId, userIds, companyId };

		await lastValueFrom(
			this.httpClient.post(`${this.env.apiUrl}/api/v1/role/assign`, body).pipe(
				catchError((error) => {
					this.loadingService.stop(ApiLoadingKeys.assignRoleToUserId);
					return ApiService.handleError(error);
				})
			)
		);
		this.loadingService.stop(ApiLoadingKeys.assignRoleToUserId);
	}

	async unassignRoleFromUserId(
		userIds: string[],
		roleId: string,
		companyId: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.unassignRoleFromUserId);
		const body = { userIds, companyId };

		await lastValueFrom(
			this.httpClient
				.post(`${this.env.apiUrl}/api/v1/role/${roleId}/unassign`, body)
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.unassignRoleFromUserId);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.unassignRoleFromUserId);
	}

	async getCompanyUsersByCompanyId(
		id: string,
		queryParams?: QueryParam[]
	): Promise<UserWithRoleName[]> {
		this.loadingService.start(ApiLoadingKeys.getCompanyUsersByCompanyId);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<UserWithRoleName[]>>(
					`${this.env.apiUrl}/api/v1/company/${id}/users`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getCompanyUsersByCompanyId);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getCompanyUsersByCompanyId);
		return resp!;
	}

	async getWhitelistUserListForCompany(
		queryParams: QueryParam[],
		companyId?: string
	): Promise<CompanyWhitelist[]> {
		this.loadingService.start(ApiLoadingKeys.getWhitelistUserListForCompany);
		const paramsWithCompany = companyId
			? [...queryParams, { param: 'companyId', value: companyId }]
			: queryParams;
		const params = this.generateHttpParams(paramsWithCompany);
		const resp = await lastValueFrom(
			this.httpClient
				.get<CompanyWhitelist[]>(
					`${this.env.apiUrl}/api/v1/whitelistUser/getCompanyWhitelist`,
					{ params }
				)
				.pipe(
					map((it) => it),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getWhitelistUserListForCompany
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getWhitelistUserListForCompany);
		return resp!;
	}

	async updateOrAddNoteFromWhitelist(
		id: string,
		updateRequest: SaveNoteRequestBody,
		companyId?: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.updateOrAddNoteFromWhitelist);
		const params = companyId
			? this.generateHttpParams([{ param: 'companyId', value: companyId }])
			: undefined;
		await lastValueFrom(
			this.httpClient
				.put(
					`${this.env.apiUrl}/api/v1/whitelistUser/addOrUpdateNote/${id}`,
					updateRequest,
					params ? { params } : undefined
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.updateOrAddNoteFromWhitelist
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.updateOrAddNoteFromWhitelist);
	}

	async updateWhitelistStatus(
		id: string,
		updateRequest: SaveStatusRequestBody,
		companyId?: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.updateWhitelistStatus);
		const params = companyId
			? this.generateHttpParams([{ param: 'companyId', value: companyId }])
			: undefined;
		await lastValueFrom(
			this.httpClient
				.put(
					`${this.env.apiUrl}/api/v1/whitelistUser/updateStatus/${id}`,
					updateRequest,
					params ? { params } : undefined
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.updateWhitelistStatus);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.updateWhitelistStatus);
	}

	async getMyUsers(queryParams?: QueryParam[]): Promise<User[]> {
		this.loadingService.start(ApiLoadingKeys.getMyUsers);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<User[]>>(`${this.env.apiUrl}/api/v1/user`, { params })
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getMyUsers);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getMyUsers);
		return resp!;
	}

	async getCompanyPropertiesByCompanyId(
		id: string,
		queryParams?: QueryParam[]
	): Promise<{ property: Property; deviceCount: number }[]> {
		this.loadingService.start(ApiLoadingKeys.getCompanyPropertiesByCompanyId);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<{ property: Property; deviceCount: number }[]>>(
					`${this.env.apiUrl}/api/v1/company/${id}/property`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getCompanyPropertiesByCompanyId
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getCompanyPropertiesByCompanyId);
		return resp!;
	}

	async getTimezone(): Promise<Timezone[]> {
		this.loadingService.start(ApiLoadingKeys.getTimezone);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<Timezone[]>>(`${this.env.apiUrl}/api/v1/timezone/`)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getTimezone);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getTimezone);
		return resp!;
	}

	async getCountriesAvailableForSms(): Promise<CountryAvailableSms> {
		this.loadingService.start(ApiLoadingKeys.getCountryAvailableSms);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<CountryAvailableSms>>(
					`${this.env.apiUrl}/api/v1/countryavailablesms/`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getCountryAvailableSms);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getCountryAvailableSms);
		return resp!;
	}

	async getBillingContracts(queryParams?: QueryParam[]): Promise<Contract[]> {
		this.loadingService.start(ApiLoadingKeys.getContracts);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<Contract[]>>(
					`${this.env.apiUrl}/api/v1/billingcontract`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getContracts);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getContracts);
		return resp!;
	}

	async getBillingContractById(id: string): Promise<{
		billingSettingsResult: Contract;
		addOnBillingSettingsResult: AddOnBillingSettings[];
	}> {
		this.loadingService.start(ApiLoadingKeys.getSingleContract);
		const resp = await lastValueFrom(
			this.httpClient
				.get<
					DataResponse<{
						billingSettingsResult: Contract;
						addOnBillingSettingsResult: AddOnBillingSettings[];
					}>
				>(`${this.env.apiUrl}/api/v1/billingcontract/${id}`)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getSingleContract);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getSingleContract);
		return resp!;
	}

	async saveBillingContract(contract: ContractSettingsFields): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.saveContract);
		await lastValueFrom(
			this.httpClient
				.post<DataResponse<ContractSettingsFields>>(
					`${this.env.apiUrl}/api/v1/billingcontract`,
					{ data: contract }
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.saveContract);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.saveContract);
	}

	async updateBillingContract(
		id: string,
		updatedContract: {
			isManuallyEnded?: boolean;
			isMonthlyAutoRenewed?: boolean;
			billingStartDate?: string;
			addOnBilling?: AddOnBillingSettings;
		}
	) {
		this.loadingService.start(ApiLoadingKeys.updateContract);
		await lastValueFrom(
			this.httpClient
				.patch<DataResponse<any>>(
					`${this.env.apiUrl}/api/v1/billingcontract/${id}`,
					{ data: updatedContract }
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.updateContract);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.updateContract);
	}

	async createNewProperty(
		requestBody: CreatePropertyRequestBody
	): Promise<Property> {
		this.loadingService.start(ApiLoadingKeys.createNewProperty);
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<Property>>(
					`${this.env.apiUrl}/api/v1/property/`,
					requestBody
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.createNewProperty);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.createNewProperty);
		return resp!;
	}

	async getProperties(
		queryParams?: QueryParam[]
	): Promise<{ property: Property; deviceCount: number }[]> {
		this.loadingService.start(ApiLoadingKeys.getProperties);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<{ property: Property; deviceCount: number }[]>>(
					`${this.env.apiUrl}/api/v1/property`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getProperties);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getProperties);
		return resp!;
	}

	async getClients(
		companyId: string,
		queryParams?: QueryParam[]
	): Promise<Client[]> {
		this.loadingService.start(ApiLoadingKeys.getClients);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<Client[]>>(
					`${this.env.apiUrl}/api/v1/company/${companyId}/clients`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getClients);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getClients);
		return resp!;
	}

	async getClientsByLocationId(
		locationId: string,
		queryParams?: QueryParam[]
	): Promise<Client[]> {
		this.loadingService.start(ApiLoadingKeys.getClientsByLocationId);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<Client[]>>(
					`${this.env.apiUrl}/api/v1/properties/${locationId}/clients`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getClientsByLocationId);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getClientsByLocationId);
		return resp!;
	}

	async getClientDetails(clientId: string): Promise<Client> {
		this.loadingService.start(ApiLoadingKeys.getClientDetails);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<Client>>(
					`${this.env.apiUrl}/api/v1/clients/${clientId}`
				)
				.pipe(
					map((it) => {
						this.loadingService.stop(ApiLoadingKeys.getClientDetails);
						return it.data;
					}),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getClientDetails);
						return ApiService.handleError(err);
					})
				)
		);
		return resp!;
	}

	async getClientProperty(
		clientId: string
	): Promise<{ property: Property; deviceCount: number }[]> {
		this.loadingService.start(ApiLoadingKeys.getClientProperty);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<{ property: Property; deviceCount: number }[]>>(
					`${this.env.apiUrl}/api/v1/client/${clientId}/property`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getClientProperty);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getClientProperty);
		return resp!;
	}

	async getClientDevices(
		clientId: string,
		queryParams?: QueryParam[]
	): Promise<{ device: Device; groupNames?: string[] }[]> {
		this.loadingService.start(ApiLoadingKeys.getClientDevices);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<{ device: Device; groupNames?: string[] }[]>>(
					`${this.env.apiUrl}/api/v1/client/${clientId}/devices`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getClientDevices);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getClientDevices);
		return resp!;
	}

	async updateClientDetails(
		clientId: string,
		updateClientPayload: {
			firstName: string | null;
			lastName: string | null;
			idn: string | null;
			active: boolean;
		}
	): Promise<{ affectedRows: number } | { installedDevices: number }> {
		this.loadingService.start(ApiLoadingKeys.updateClientDetails);
		const resp = await lastValueFrom(
			this.httpClient
				.put<DataResponse<{ affectedRows: number }>>(
					`${this.env.apiUrl}/api/v1/clients/update/${clientId}`,
					updateClientPayload
				)
				.pipe(
					map((it) => {
						this.loadingService.stop(ApiLoadingKeys.updateClientDetails);
						return it.data;
					}),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.updateClientDetails);
						return ApiService.handleError(err);
					})
				)
		);
		return resp;
	}

	async createNewClient(createClientPayload: {
		firstName?: string;
		lastName?: string;
		idn?: string;
		locationId?: string;
		active: boolean;
	}): Promise<Client> {
		this.loadingService.start(ApiLoadingKeys.createNewClient);
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<Client>>(
					`${this.env.apiUrl}/api/v1/clients`,
					createClientPayload
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.createNewClient);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.createNewClient);
		return resp!;
	}

	async createClientWithNewProperty(
		client: AddClientWithNewPropertyRequestBody,
		isConfirmed: boolean
	): Promise<AddClientWithNewPropertyBody> {
		this.loadingService.start(ApiLoadingKeys.createClientWithNewProperty);
		const url = !isConfirmed
			? `${this.env.apiUrl}/api/v1/clients/create-with-new-property`
			: `${this.env.apiUrl}/api/v1/clients/create-with-new-property?isConfirmed=${isConfirmed}`;
		return await lastValueFrom(
			this.httpClient
				.post<DataResponse<AddClientWithNewPropertySuccessResponse>>(
					url,
					client
				)
				.pipe(
					map((it) => {
						this.loadingService.stop(
							ApiLoadingKeys.createClientWithNewProperty
						);
						return it.data as AddClientWithNewPropertyBody;
					}),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.createClientWithNewProperty
						);
						return ApiService.handleError(err);
					})
				)
		);
	}

	// Get : contacts for the notifications :
	async getContactList(
		clientId: string,
		queryParams?: QueryParam[]
	): Promise<Contacts[]> {
		this.loadingService.start(ApiLoadingKeys.getContacts);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<Contacts[]>>(
					`${this.env.apiUrl}/api/v1/contact/list/${clientId}`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getContacts);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getContacts);

		return resp!;
	}

	// Post : Add / Edit contact details for notifications :
	async postContactDetails(
		addEditFormData: AddEditContactDetailRequest
	): Promise<AddEditDeleteContactDetailResponse> {
		this.loadingService.start(ApiLoadingKeys.addEditContacts);
		const resp = await lastValueFrom(
			this.httpClient
				.post(
					`${this.env.apiUrl}/api/v1/contact/add-update-details`,
					addEditFormData
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.addEditContacts);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.addEditContacts);

		return resp! as unknown as AddEditDeleteContactDetailResponse;
	}

	// Delete : contact details for notifications :
	async deleteContactDetail(
		id: string,
		clientId: string
	): Promise<AddEditDeleteContactDetailResponse> {
		this.loadingService.start(ApiLoadingKeys.deleteContact);
		const resp = await lastValueFrom(
			this.httpClient
				.delete(`${this.env.apiUrl}/api/v1/contact/remove/${clientId}/${id}`)
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.deleteContact);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.deleteContact);

		return resp! as unknown as AddEditDeleteContactDetailResponse;
	}

	async getProperty(propertyId: string): Promise<Property> {
		this.loadingService.start(ApiLoadingKeys.getProperty);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<Property>>(
					`${this.env.apiUrl}/api/v1/property/${propertyId}`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getProperty);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getProperty);
		return resp!;
	}

	async getPropertyEventHistory(
		id: string,
		queryParams?: QueryParam[]
	): Promise<EventHistory[]> {
		this.loadingService.start(ApiLoadingKeys.getPropertyEventHistory);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<EventHistoryResponse>(
					`${this.env.apiUrl}/api/v1/properties/${id}/eventHistory`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getPropertyEventHistory);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getPropertyEventHistory);
		return resp!;
	}

	async editPropertyById(
		id: string,
		editRequest: EditPropertyRequestBody
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.editPropertyById);
		await lastValueFrom(
			this.httpClient
				.put(`${this.env.apiUrl}/api/v1/property/${id}`, editRequest)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.editPropertyById);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.editPropertyById);
	}

	async editDeviceById(
		deviceId: string,
		editRequest: EditDeviceRequestBody
	): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.editDeviceById);
		const resp = await lastValueFrom(
			this.httpClient
				.put<DeviceEditResponse>(
					`${this.env.apiUrl}/api/v1/device/${deviceId}`,
					editRequest
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.editDeviceById);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.editDeviceById);
		return resp!.updateSuccessful;
	}

	async getAllDevices(
		queryParams?: QueryParam[]
	): Promise<
		{ device: Device; groupNames?: string[]; source?: DeviceAccessSource }[]
	> {
		this.loadingService.start(ApiLoadingKeys.getAllDevices);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<{ device: Device; groupNames?: string[] }[]>>(
					`${this.env.apiUrl}/api/v1/device`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getAllDevices);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAllDevices);
		return resp!;
	}

	// async getUsers(queryParameters?: QueryParam[]): Promise<User[]> {
	//   this.loadingService.start(ApiLoadingKeys.getAllUsers);
	//   const params = this.generateHttpParams(queryParameters);
	//   const resp = await lastValueFrom(this.httpClient
	//     .get<DataResponse<User[]>>(`${this.env.apiUrl}/api/v1/user`, { params })
	//     .pipe(
	//       map(it => it.data),
	//       catchError((err) => {
	//         this.loadingService.stop(ApiLoadingKeys.getAllUsers);
	//         return ApiService.handleError(err);
	//       })
	//     ));
	//   this.loadingService.stop(ApiLoadingKeys.getAllUsers);
	//   return resp!;
	// }

	async getDevicesByCompanyId(
		companyId: string,
		queryParams?: QueryParam[]
	): Promise<
		{
			device: Device;
			groupNames?: string[];
			source?: DeviceAccessSource;
		}[]
	> {
		this.loadingService.start(ApiLoadingKeys.getDevicesByCompanyId);
		const url = `${this.env.apiUrl}/api/v1/company/${companyId}/device`;
		const params = this.generateHttpParams(queryParams);

		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<{ device: Device; groupNames?: string[] }[]>>(url, {
					params,
				})
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDevicesByCompanyId);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDevicesByCompanyId);
		return resp!;
	}

	async getDevicesByPropertyId(
		propertyId: string,
		queryParams?: QueryParam[]
	): Promise<{ device: Device; groupNames?: string[] }[]> {
		this.loadingService.start(ApiLoadingKeys.getDevicesByPropertyId);
		const url = `${this.env.apiUrl}/api/v1/properties/${propertyId}/device`;
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<{ device: Device; groupNames?: string[] }[]>>(url, {
					params,
				})
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDevicesByPropertyId);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDevicesByPropertyId);
		return resp!;
	}

	async getDevicesByDeviceGroupId(
		deviceGroupId: string,
		queryParams?: QueryParam[]
	): Promise<DevicesResponse> {
		const params = this.generateHttpParams(queryParams);
		if (!params.has('rowIndex') || !params.has('rowCount')) {
			return {
				data: [],
			};
		}
		this.loadingService.start(ApiLoadingKeys.getDevicesByDeviceGroupId);
		const url = `${this.env.apiUrl}/api/v1/deviceGroup/${deviceGroupId}/device`;
		const resp = await lastValueFrom(
			this.httpClient.get<DataResponse<DevicesResponse>>(url, { params }).pipe(
				map((it) => it),
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.getDevicesByDeviceGroupId);
					return ApiService.handleError(err);
				})
			)
		);
		this.loadingService.stop(ApiLoadingKeys.getDevicesByDeviceGroupId);
		return resp! as unknown as DevicesResponse;
	}

	async getAvailableDevices(
		deviceGroupId: string,
		queryParams?: QueryParam[]
	): Promise<{ device: Device; groupNames?: string[] }[]> {
		this.loadingService.start(ApiLoadingKeys.getAvailableDevices);
		const url = `${this.env.apiUrl}/api/v1/deviceGroup/${deviceGroupId}/device/available`;
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<{ device: Device; groupNames?: string[] }[]>>(url, {
					params,
				})
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getAvailableDevices);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAvailableDevices);
		return resp!;
	}

	async getDeviceById(deviceId: string): Promise<Device> {
		this.loadingService.start(ApiLoadingKeys.getDeviceById);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DeviceDetailResponse>(
					`${this.env.apiUrl}/api/v1/device/${deviceId}`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceById);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceById);
		return resp!;
	}

	async getWasDeviceOwner(deviceId: string): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.getWasDeviceOwner);
		const resp = await lastValueFrom(
			this.httpClient
				.get<any>(`${this.env.apiUrl}/api/v1/wasDeviceOwner/${deviceId}`)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getWasDeviceOwner);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getWasDeviceOwner);
		return resp;
	}

	async getDeviceBySerialNumber(serialNumber: string): Promise<Device> {
		this.loadingService.start(ApiLoadingKeys.getDeviceBySerialNumber);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DeviceDetailResponse>(
					`${this.env.apiUrl}/api/v1/device/serialNumber/${serialNumber}`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceBySerialNumber);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceBySerialNumber);
		return resp!;
	}

	async getDeviceInstallationPhotos(
		deviceId: string
	): Promise<DeviceInstallationPhoto[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceInstallationPhotos);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<DeviceInstallationPhoto[]>>(
					`${this.env.apiUrl}/api/v1/device/${deviceId}/installationPhoto`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getDeviceInstallationPhotos
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceInstallationPhotos);
		return resp!;
	}

	async saveDeviceInstallationPhoto(
		deviceId: string,
		file: File,
		caption: string
	): Promise<DeviceInstallationPhoto> {
		this.loadingService.start(ApiLoadingKeys.saveDeviceInstallationPhoto);
		const formData = new FormData();
		formData.append('file', file);
		formData.append('caption', caption);
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<DeviceInstallationPhoto>>(
					`${this.env.apiUrl}/api/v1/device/${deviceId}/installationPhoto`,
					formData
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.saveDeviceInstallationPhoto
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.saveDeviceInstallationPhoto);
		return resp!;
	}

	async getAppNotes(
		deviceId: string,
		queryParams: QueryParam[]
	): Promise<
		{
			appVisit: AppVisit;
			appVisitNote: AppVisitNote;
			user: User;
			appVisitPhotos: AppVisitPhoto[];
		}[]
	> {
		this.loadingService.start(ApiLoadingKeys.getAppNotes);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<
					DataResponse<
						{
							appVisit: AppVisit;
							appVisitNote: AppVisitNote;
							user: User;
							appVisitPhotos: AppVisitPhoto[];
						}[]
					>
				>(`${this.env.apiUrl}/api/v1/device/${deviceId}/appNote`, { params })
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getAppNotes);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAppNotes);
		return resp!;
	}

	async getDeviceNotes(
		queryParams: QueryParam[],
		deviceId: string
	): Promise<DeviceNoteResponse> {
		const apiURL = `${this.env.apiUrl}/api/v1/device/${deviceId}/deviceNote`;

		this.loadingService.start(ApiLoadingKeys.getAllDeviceNotes);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient.get<any>(apiURL, { params }).pipe(
				map((it) => it),
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.getAllDeviceNotes);
					return ApiService.handleError(err);
				})
			)
		);
		this.loadingService.stop(ApiLoadingKeys.getAllDeviceNotes);
		return resp!;
	}

	async getAllDeviceNotes(
		queryParams: QueryParam[]
	): Promise<DeviceNoteResponse> {
		const apiURL = `${this.env.apiUrl}/api/v1/getAllNote`;

		this.loadingService.start(ApiLoadingKeys.getAllDeviceNotes);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient.get<any>(apiURL, { params }).pipe(
				map((it) => it),
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.getAllDeviceNotes);
					return ApiService.handleError(err);
				})
			)
		);
		this.loadingService.stop(ApiLoadingKeys.getAllDeviceNotes);
		return resp!;
	}

	async getDeviceGroups(queryParams?: QueryParam[]): Promise<
		{
			group: DeviceGroup;
			deviceCount?: number;
			controlAssignmentCount?: number;
			source?: { companyId?: string; companyName?: string };
		}[]
	> {
		this.loadingService.start(ApiLoadingKeys.getDeviceGroups);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<
					DataResponse<
						{
							group: DeviceGroup;
							deviceCount?: number;
							controlAssignmentCount?: number;
							source?: { companyId?: string; companyName?: string };
						}[]
					>
				>(`${this.env.apiUrl}/api/v1/deviceGroup`, { params })
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceGroups);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceGroups);
		return resp!;
	}

	async getDeviceGroupsByCompanyId(
		companyId: string,
		queryParams?: QueryParam[]
	): Promise<
		{
			group: DeviceGroup;
			deviceCount?: number;
			controlAssignmentCount?: number;
			source?: { companyId?: string; companyName?: string };
		}[]
	> {
		this.loadingService.start(ApiLoadingKeys.getDeviceGroupsByCompanyId);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<
					DataResponse<
						{
							group: DeviceGroup;
							deviceCount?: number;
							controlAssignmentCount?: number;
							source?: { companyId?: string; companyName?: string };
						}[]
					>
				>(`${this.env.apiUrl}/api/v1/company/${companyId}/deviceGroup`, {
					params,
				})
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceGroupsByCompanyId);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceGroupsByCompanyId);
		return resp!;
	}

	async getDeviceGroupOptions(queryParams?: QueryParam[]): Promise<
		{
			group: DeviceGroup;
			deviceCount?: number;
			controlAssignmentCount?: number;
			source?: { companyId?: string; companyName?: string };
		}[]
	> {
		this.loadingService.start(ApiLoadingKeys.getDeviceGroupOptions);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<
					DataResponse<
						{
							group: DeviceGroup;
							deviceCount?: number;
							controlAssignmentCount?: number;
							source?: { companyId?: string; companyName?: string };
						}[]
					>
				>(`${this.env.apiUrl}/api/v1/deviceGroupOptions`, { params })
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceGroupOptions);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceGroupOptions);
		return resp!;
	}

	async getDeviceGroupById(deviceGroupId: string): Promise<DeviceGroup> {
		this.loadingService.start(ApiLoadingKeys.getDeviceGroupById);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<DeviceGroup>>(
					`${this.env.apiUrl}/api/v1/deviceGroup/${deviceGroupId}`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceGroupById);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceGroupById);
		return resp!;
	}

	async getInheritedDeviceGroupControlById(
		deviceGroupId: string,
		deviceId: string,
		childControlIds?: string[]
	): Promise<
		{
			control: DeviceControl;
			assignments: ControlAssignment[];
			devices: Device[];
		}[]
	> {
		this.loadingService.start(
			ApiLoadingKeys.getInheritedDeviceGroupControlById
		);

		// Build query parameters if childControlIds is provided
		const params: any = {};
		if (childControlIds && childControlIds.length > 0) {
			params.childControlIds = childControlIds;
		}

		// Used for device or device group grant access form (for both edit and create)
		const resp = await lastValueFrom(
			this.httpClient
				.get<
					DataResponse<
						{
							control: DeviceControl;
							assignments: ControlAssignment[];
							devices: Device[];
						}[]
					>
				>(
					`${this.env.apiUrl}/api/v1/deviceGroup/${deviceGroupId}/${deviceId}/inheritedControl`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getInheritedDeviceGroupControlById
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getInheritedDeviceGroupControlById);
		return resp!;
	}

	async getInheritedDeviceGroupControlByDeviceId(deviceId: string): Promise<
		{
			control: DeviceControl;
			assignments: ControlAssignment[];
			devices: Device[];
		}[]
	> {
		this.loadingService.start(
			ApiLoadingKeys.getInheritedDeviceGroupControlById
		);
		const resp = await lastValueFrom(
			this.httpClient
				.get<
					DataResponse<
						{
							control: DeviceControl;
							assignments: ControlAssignment[];
							devices: Device[];
						}[]
					>
				>(`${this.env.apiUrl}/api/v1/device/${deviceId}/inheritedControl`)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getInheritedDeviceGroupControlById
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getInheritedDeviceGroupControlById);
		return resp!;
	}

	async getDeviceControls(
		queryParams?: QueryParam[]
	): Promise<{ deviceControl: DeviceControl }[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceControls);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<{ deviceControl: DeviceControl }[]>>(
					`${this.env.apiUrl}/api/v1/deviceControl`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceControls);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceControls);
		return resp!;
	}

	async transferDeviceOwnership(
		deviceIds: string[],
		companyId: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.transferDeviceOwnership);
		await lastValueFrom(
			this.httpClient
				.post(`${this.env.apiUrl}/api/v1/device/transferDeviceOwnership`, {
					deviceIds,
					companyId,
				})
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.transferDeviceOwnership);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.transferDeviceOwnership);
	}

	async massTransferDeviceOwnership(
		deviceSerialNumbers: string[],
		companyId: string
	): Promise<TransferDeviceOwnershipError[]> {
		this.loadingService.start(ApiLoadingKeys.transferDeviceOwnership);
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<any>>(
					`${this.env.apiUrl}/api/v1/device/transferDeviceOwnership`,
					{ deviceSerialNumbers, companyId }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.transferDeviceOwnership);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.transferDeviceOwnership);
		return resp!.transferErrors;
	}

	async getControlAssignments(
		queryParams?: QueryParam[]
	): Promise<ControlAssignment[]> {
		this.loadingService.start(ApiLoadingKeys.getControlAssignments);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<ControlAssignment[]>>(
					`${this.env.apiUrl}/api/v1/deviceControl/assignment`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getControlAssignments);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getControlAssignments);
		return resp!;
	}

	async deleteControlAssignments(
		controlAssignmentIds: string[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.deleteControlAssignments);
		await lastValueFrom(
			this.httpClient
				.post(`${this.env.apiUrl}/api/v1/deviceControl/assignment/delete`, {
					controlAssignmentIds,
				})
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.deleteControlAssignments);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.deleteControlAssignments);
	}

	async editControlAssignments(
		controlAssignmentIds: string[],
		utcStartDate: string,
		utcEndDate?: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.editControlAssignments);
		await lastValueFrom(
			this.httpClient
				.put(`${this.env.apiUrl}/api/v1/controlAssignment`, {
					controlAssignmentIds,
					utcStartDate,
					utcEndDate,
				})
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.editControlAssignments);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.editControlAssignments);
	}

	async editControlAssignmentsFull(
		controlAssignmentIds: string[],
		editControlAssignment: Components.Schemas.EditControlAssignment
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.editControlAssignments);
		const body: Components.RequestBodies.EditControlAssignmentsRequestBody = {
			controlAssignmentIds,
			editControlAssignment,
		};
		await lastValueFrom(
			this.httpClient
				.put(`${this.env.apiUrl}/api/v2/controlAssignment`, body)
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.editControlAssignments);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.editControlAssignments);
	}

	async getControlAssignmentOptions(
		type: AssignableRelationshipType,
		search?: string
	): Promise<ControlAssignmentOption[]> {
		this.loadingService.start(ApiLoadingKeys.getControlAssignmentOptions);
		const queryParams: QueryParam[] = [{ param: 'type', value: type }];
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<ControlAssignmentOption[]>>(
					`${this.env.apiUrl}/api/v1/deviceControl/assignment/available`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getControlAssignmentOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getControlAssignmentOptions);
		return resp!;
	}

	async createDeviceControlsAndControlAssignments(
		deviceControl: DeviceControl,
		controlAssignments: ControlAssignment[],
		parentControlAssignmentId?: string
	): Promise<void> {
		const body: Components.RequestBodies.CreateDeviceControlAndControlAssignmentsRequestBody =
			{ deviceControl, controlAssignments, parentControlAssignmentId };

		this.loadingService.start(
			ApiLoadingKeys.createDeviceControlsAndControlAssignments
		);
		const resp = await lastValueFrom(
			this.httpClient
				.post<void>(`${this.env.apiUrl}/api/v1/controlAssignment`, body)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.createDeviceControlsAndControlAssignments
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(
			ApiLoadingKeys.createDeviceControlsAndControlAssignments
		);
	}

	async getOneDayCode(
		id: string,
		queryParams?: QueryParam[]
	): Promise<OneDayCodeListResponse[]> {
		this.loadingService.start(ApiLoadingKeys.getOneDayCode);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<OneDayCodeListResponse[]>>(
					`${this.env.apiUrl}/api/v1/device/${id}/oneDayCode`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getOneDayCode);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getOneDayCode);
		return resp!;
	}

	async getAccessCode(
		id: string,
		queryParams?: QueryParam[]
	): Promise<AccessCodeListResponse[]> {
		this.loadingService.start(ApiLoadingKeys.getAccessCode);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<AccessCodeListResponse[]>>(
					`${this.env.apiUrl}/api/v1/device/${id}/accessCode`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getAccessCode);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAccessCode);
		return resp!;
	}

	async generateSingleDeviceCodeGenReportCsv(
		fileName: string,
		id: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.getAccessCode);
		let params = this.generateHttpParams(queryParams);
		// Add user's language locale to query parameters
		params = params.set('language', this.localeService.getLocale());

		this.httpClient
			.get(`${this.env.apiUrl}/api/v1/report/${id}/accessCode/csv`, {
				responseType: 'text',
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.getAccessCode);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				const blob = new Blob([response], { type: 'text/csv' });
				const url = window.URL.createObjectURL(blob);
				const fileLink = document.createElement('a');
				this.loadingService.stop(ApiLoadingKeys.getAccessCode);
				fileLink.href = url;
				fileLink.download = fileName;
				fileLink.click();
			});
	}

	async requestAndSaveOneDayCode(
		id: string,
		oneDayCodeGen3: OneDayCodeGen3,
		notifyByEmail: boolean,
		includeInEmail: {
			address: boolean;
			propertyName: boolean;
			deviceLocation: boolean;
		},
		notifyBySms: boolean,
		includeInSms: {
			address: boolean;
			propertyName: boolean;
			deviceLocation: boolean;
		}
	): Promise<OneDayCodeResponse> {
		this.loadingService.start(ApiLoadingKeys.requestAndSaveOneDayCode);
		const resp = await lastValueFrom(
			this.httpClient
				.post<OneDayCodeResponse>(
					`${this.env.apiUrl}/api/v1/device/${id}/oneDayCode`,
					{
						oneDayCodeGen3,
						notifyByEmail,
						includeInEmail,
						notifyBySms,
						includeInSms,
					}
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.requestAndSaveOneDayCode);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.requestAndSaveOneDayCode);
		return resp!;
	}

	async getFlexCode(
		id: string,
		queryParams?: QueryParam[]
	): Promise<FlexCodeListResponse[]> {
		this.loadingService.start(ApiLoadingKeys.getFlexCode);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<FlexCodeListResponse[]>>(
					`${this.env.apiUrl}/api/v1/device/${id}/flexCode`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getFlexCode);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getFlexCode);
		return resp!;
	}

	async requestAndSaveFlexCode(
		id: string,
		flexCodeGen4: FlexCodeGen4,
		notifyByEmail: boolean,
		includeInEmail: {
			address: boolean;
			propertyName: boolean;
			deviceLocation: boolean;
		},
		notifyBySms: boolean,
		includeInSms: {
			address: boolean;
			propertyName: boolean;
			deviceLocation: boolean;
		}
	): Promise<FlexCodeGen4> {
		this.loadingService.start(ApiLoadingKeys.requestAndSaveFlexCode);
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<FlexCodeGen4>>(
					`${this.env.apiUrl}/api/v1/device/${id}/flexCode`,
					{
						flexCodeGen4,
						notifyByEmail,
						includeInEmail,
						notifyBySms,
						includeInSms,
					}
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.requestAndSaveFlexCode);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.requestAndSaveFlexCode);
		return resp!;
	}

	async requestAndSaveHaspCode(
		id: string,
		haspCode: HaspCodeFields
	): Promise<HaspCodeFields> {
		this.loadingService.start(ApiLoadingKeys.requestAndSaveHaspCode);
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<HaspCodeFields>>(
					`${this.env.apiUrl}/api/v1/device/${id}/haspCode`,
					{ haspCode }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.requestAndSaveHaspCode);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.requestAndSaveHaspCode);
		return resp!;
	}

	async requestDeviceRemovalAction(
		deviceId: string,
		action: DeviceRemovalActions
	): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.requestDeviceRemovalAction);
		const resp = await lastValueFrom(
			this.httpClient
				.put(
					`${this.env.apiUrl}/api/v1/device/${deviceId}/removal/${action}`,
					null
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.requestDeviceRemovalAction);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.requestDeviceRemovalAction);
		return resp!;
	}

	async getTransferEligibleDevices(
		excludeDeviceId: string,
		serialNumber?: string,
		rowIndex?: number,
		rowCount?: number
	): Promise<Device[]> {
		const params = this.generateHttpParams([
			{ param: 'excludeDeviceId', value: excludeDeviceId },
			...(serialNumber ? [{ param: 'serialNumber', value: serialNumber }] : []),
			...(rowIndex !== undefined
				? [{ param: 'rowIndex', value: rowIndex.toString() }]
				: []),
			...(rowCount !== undefined
				? [{ param: 'rowCount', value: rowCount.toString() }]
				: []),
		]);

		const resp = await lastValueFrom(
			this.httpClient
				.get<DevicesResponse>(
					`${this.env.apiUrl}/api/v1/device/transfer-eligible`,
					{ params }
				)
				.pipe(
					catchError((err) => {
						return ApiService.handleError(err);
					})
				)
		);

		return resp?.data?.map((d) => d.device!) ?? [];
	}

	async transferDeviceAccess(
		sourceDeviceId: string,
		targetDeviceId: string,
		haspFirstName?: string,
		haspLastName?: string,
		haspEmail?: string
	): Promise<{
		success: boolean;
		sourceDeviceHaspCode?: string;
		sourceDeviceType?: string;
		targetDeviceHaspCode?: string;
		targetDeviceType?: string;
	}> {
		const body = {
			sourceDeviceId,
			targetDeviceId,
			...(haspFirstName && { haspFirstName }),
			...(haspLastName && { haspLastName }),
			...(haspEmail && { haspEmail }),
		};

		const resp = await lastValueFrom(
			this.httpClient
				.post<{
					success: boolean;
					sourceDeviceHaspCode?: string;
					sourceDeviceType?: string;
					targetDeviceHaspCode?: string;
					targetDeviceType?: string;
				}>(`${this.env.apiUrl}/api/v1/device/transfer-access`, body)
				.pipe(
					catchError((err) => {
						return ApiService.handleError(err);
					})
				)
		);

		return resp;
	}

	async getDeviceEventHistory(
		id: string,
		queryParams?: QueryParam[]
	): Promise<EventHistory[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceEventHistory);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<EventHistoryResponse>(
					`${this.env.apiUrl}/api/v1/device/${id}/eventHistory`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceEventHistory);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceEventHistory);
		return resp!;
	}

	async getEventHistoryReport(
		id: string,
		type: 'device' | 'user' | 'properties',
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.getEventHistoryReport);
		let params = this.generateHttpParams(queryParams);
		// Add user's language locale to query parameters
		params = params.set('language', this.localeService.getLocale());
		this.httpClient
			.get(`${this.env.apiUrl}/api/v1/${type}/${id}/eventHistory/csv`, {
				responseType: 'text',
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.getEventHistoryReport);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				const blob = new Blob([response], { type: 'text/csv' });
				const url = window.URL.createObjectURL(blob);
				const fileLink = document.createElement('a');
				this.loadingService.stop(ApiLoadingKeys.getEventHistoryReport);
				fileLink.href = url;
				fileLink.download = 'report';
				fileLink.click();
			});
	}

	async registerNewUser(
		users: User[],
		parentEntityId: string,
		roleId?: string,
		roleName?: string,
		systemCapabilityItems?: SystemCapabilityItem[]
	): Promise<NewUserResponse> {
		this.loadingService.start(ApiLoadingKeys.registerNewUser);
		const systemCapabilities = systemCapabilityItems
			?.filter((it) => it.granted === true)
			.map((it) => it.systemCapability.id);

		const body = {
			users,
			parentEntityId,
			roleId,
			roleName,
			systemCapabilities,
		};

		const resp = await lastValueFrom(
			this.httpClient
				.post<NewUserResponse>(
					`${this.env.apiUrl}/api/v1/user/registration`,
					body
				)
				.pipe(
					map((it) => {
						if (it.data.userIds.length > 0) {
							this.analytics.logEvent('record_created', {
								record_type: 'user',
							});
						}
						return it;
					}),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.registerNewUser);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.registerNewUser);
		return resp!;
	}

	async checkNewUserRegistrationToken(token: string): Promise<TokenValidity> {
		this.loadingService.start(ApiLoadingKeys.checkNewUserRegistrationToken);
		const resp = await lastValueFrom(
			this.httpClient
				.get<{
					data: TokenValidity;
				}>(
					`${this.env.apiUrl}/api/v1/user/registration/token/check?token=${token}`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.checkNewUserRegistrationToken
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.checkNewUserRegistrationToken);
		return resp!;
	}

	async saveMobilePhoneNumber(
		userId: string,
		phoneNumber: PhoneNumber
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.saveMobilePhoneNumber);
		await lastValueFrom(
			this.httpClient
				.put(`${this.env.apiUrl}/api/v1/user/${userId}/phone`, { phoneNumber })
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.saveMobilePhoneNumber);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.saveMobilePhoneNumber);
	}

	async saveEmail(userId: string, newEmail: string): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.saveEmail);
		await lastValueFrom(
			this.httpClient
				.put(`${this.env.apiUrl}/api/v1/user/${userId}/email`, { newEmail })
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.saveEmail);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.saveEmail);
	}

	async getSecurityQuestions(
		countryCode: string | undefined
	): Promise<string[]> {
		this.loadingService.start(ApiLoadingKeys.getSecurityQuestions);
		let url = `${this.env.apiUrl}/api/v1/user/securityQuestion/list`;
		if (countryCode) {
			url = url + `?countryCode=${countryCode}`;
		}
		const resp = await lastValueFrom(
			this.httpClient.get<{ data: string[] }>(`${url}`).pipe(
				map((it) => it.data),
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.getSecurityQuestions);
					return ApiService.handleError(err);
				})
			)
		);
		this.loadingService.stop(ApiLoadingKeys.getSecurityQuestions);
		return resp!;
	}

	async getSecurityAnswers(userId: string): Promise<UserSecurityAnswer[]> {
		this.loadingService.start(ApiLoadingKeys.getSecurityAnswers);
		const resp = await lastValueFrom(
			this.httpClient
				.get<{
					data: UserSecurityAnswer[];
				}>(`${this.env.apiUrl}/api/v1/user/${userId}/securityQuestion/answer`)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getSecurityAnswers);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getSecurityAnswers);
		return resp!;
	}

	async requestPhoneVerificationCode(
		userId: string,
		phoneNumber: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.requestPhoneVerificationCode);
		await lastValueFrom(
			this.httpClient
				.post(`${this.env.apiUrl}/api/v1/user/${userId}/phone/requestToken`, {
					phoneNumber,
				})
				.pipe(
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.requestPhoneVerificationCode
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.requestPhoneVerificationCode);
	}

	async verifyPhoneVerificationCode(
		userId: string,
		verificationCode: NumericToken,
		phoneNumber: string,
		countryCode?: string
	): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.verifyPhoneVerificationCode);
		const resp = await lastValueFrom(
			this.httpClient
				.post<{
					data: { valid: boolean };
				}>(
					`${this.env.apiUrl}/api/v1/user/${userId}/phone/verify?token=${verificationCode}`,
					{ phoneNumber, countryCode }
				)
				.pipe(
					map((it) => it.data.valid),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.verifyPhoneVerificationCode
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.verifyPhoneVerificationCode);
		return resp!;
	}

	async completeNewUserRegistration(
		password: string,
		token: string,
		userId: string,
		firstName: string,
		lastName: string,
		securityQuestion: string,
		securityAnswer: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.completeNewUserRegistration);
		// Complete the user registration flow by setting their password via the token
		const url = `${this.env.apiUrl}/api/v1/user/${userId}/registration/submit`;
		await lastValueFrom(
			this.httpClient
				.put(
					url,
					{
						password,
						token,
						firstName,
						lastName,
						securityQuestion,
						securityAnswer,
					},
					{ observe: 'response' }
				)
				.pipe(
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.completeNewUserRegistration
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.completeNewUserRegistration);
	}

	async updateDeviceSettings(
		deviceId: string,
		deviceSettings: {
			keyDetection?: boolean;
			ajaxHubId?: string;
		}
	): Promise<DeviceSettings | undefined> {
		this.loadingService.start(ApiLoadingKeys.updateDeviceSettings);
		const url = `${this.env.apiUrl}/api/v1/device/${deviceId}/deviceSettings`;
		const resp = await lastValueFrom(
			this.httpClient
				.put<DeviceSettingsResponse>(url, deviceSettings, {
					observe: 'response',
				})
				.pipe(
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.updateDeviceSettings);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.updateDeviceSettings);
		return resp?.body?.data;
	}

	/**
	 * An endpoint that checks if the users passed in can be added to the deviceGroup referenced.
	 * The check is to limit the number of devices users can have access to at one time.
	 *
	 * @param deviceGroupId - The device group id in which these users will be added to
	 * @param controlAssignmentOptions - The control assignment options that will be added to the device group
	 * @returns {Promise<CompanyUserDeviceGroupDeviceCount[]>} - An users device count for the device group and company and its device limit
	 */
	async verifyUsersCanBeAssignedToDeviceGroup(
		deviceGroupId: string,
		entityRelationshipIds: string[]
	): Promise<CompanyUserDeviceGroupDeviceCount[]> {
		const body: any = {
			deviceGroupId,
			entityRelationshipIds,
		};
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<CompanyUserDeviceGroupDeviceCount[]>>(
					`${this.env.apiUrl}/api/v1/deviceControl/canUsersBeAssignedToDeviceGroup`,
					body
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						return ApiService.handleError(err);
					})
				)
		);
		return resp!;
	}

	async editDeviceControl(
		deviceControlId: string,
		control: DeviceControl
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.editDeviceControl);
		await lastValueFrom(
			this.httpClient
				.put(
					`${this.env.apiUrl}/api/v1/deviceControl/${deviceControlId}`,
					control
				)
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.editDeviceControl);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.editDeviceControl);
	}

	async getCodeGenReportFilterOptions(): Promise<CodeGenReportFilterOptions> {
		this.loadingService.start(ApiLoadingKeys.getCodeGenReportFilterOptions);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<CodeGenReportFilterOptions>>(
					`${this.env.apiUrl}/api/v1/report/codeGenReport/filterOptions`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getCodeGenReportFilterOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getCodeGenReportFilterOptions);
		return resp!;
	}

	async generateCodeGenReport(
		queryParams?: QueryParam[]
	): Promise<CodeGenReport[]> {
		this.loadingService.start(ApiLoadingKeys.generateCodeGenReport);
		const params = this.generateHttpParams(queryParams);

		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<CodeGenReport[]>>(
					`${this.env.apiUrl}/api/v1/report/codeGenReport`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.generateCodeGenReport);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.generateCodeGenReport);
		return resp!;
	}

	async generateCodeGenReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generateCodeGenReportCsv);
		let params = this.generateHttpParams(queryParams);
		// Add user's language locale to query parameters
		params = params.set('language', this.localeService.getLocale());

		this.httpClient
			.get(`${this.env.apiUrl}/api/v1/report/codeGenReport/csv`, {
				responseType: 'text',
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.generateCodeGenReportCsv);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				const blob = new Blob([response], { type: 'text/csv' });
				const url = window.URL.createObjectURL(blob);
				const fileLink = document.createElement('a');
				this.loadingService.stop(ApiLoadingKeys.generateCodeGenReportCsv);
				fileLink.href = url;
				fileLink.download = fileName;
				fileLink.click();
			});
	}

	async getDeviceFilterOptions(): Promise<AccessReportFilterOptions> {
		this.loadingService.start(ApiLoadingKeys.getDeviceFilterOptions);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<AccessReportFilterOptions>>(
					`${this.env.apiUrl}/api/v1/report/device/filterOptions`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceFilterOptions);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceFilterOptions);
		return resp!;
	}

	async getAccessReportFilterOptions(): Promise<AccessReportFilterOptions> {
		this.loadingService.start(ApiLoadingKeys.getAccessReportFilterOptions);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<AccessReportFilterOptions>>(
					`${this.env.apiUrl}/api/v1/report/accessReport/filterOptions`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getAccessReportFilterOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAccessReportFilterOptions);
		return resp!;
	}

	async generateAccessReport(
		queryParams?: QueryParam[]
	): Promise<AccessReportRecord[]> {
		this.loadingService.start(ApiLoadingKeys.generateAccessReport);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<AccessReportRecord[]>>(
					`${this.env.apiUrl}/api/v1/report/accessReport`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.generateAccessReport);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.generateAccessReport);
		return resp!;
	}

	/** Starts an Access Report CSV export with these filters. The file is prepared by the server and downloaded later. */
	async startAccessReportExport(
		queryParams?: QueryParam[]
	): Promise<AccessReportExportStart> {
		this.loadingService.start(ApiLoadingKeys.startAccessReportExport);
		let params = this.generateHttpParams(queryParams);
		// Add user's language locale to query parameters
		params = params.set('language', this.localeService.getLocale());
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<AccessReportExportStart>>(
					`${this.env.apiUrl}/api/v1/report/accessReport/export`,
					null,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.startAccessReportExport);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.startAccessReportExport);
		return resp!;
	}

	async getAccessReportExport(): Promise<AccessReportExportStatus> {
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<AccessReportExportStatus>>(
					`${this.env.apiUrl}/api/v1/report/accessReport/export`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => ApiService.handleError(err))
				)
		);
		return resp!;
	}

	async downloadAccessReportExport(): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.downloadAccessReportExport);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<{ url: string }>>(
					`${this.env.apiUrl}/api/v1/report/accessReport/export/download`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.downloadAccessReportExport);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.downloadAccessReportExport);
		const fileLink = document.createElement('a');
		fileLink.href = resp!.url;
		fileLink.click();
	}

	async getKeyDetectionReportFilterOptions(): Promise<KeyDetectionReportFilterOptions> {
		this.loadingService.start(
			ApiLoadingKeys.getKeyDetectionReportFilterOptions
		);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<KeyDetectionReportFilterOptions>>(
					`${this.env.apiUrl}/api/v1/report/keyDetectionReport/filterOptions`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getKeyDetectionReportFilterOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getKeyDetectionReportFilterOptions);
		return resp!;
	}

	async generateKeyDetectionReport(
		queryParams?: QueryParam[]
	): Promise<KeyDetectionReportRecord[]> {
		this.loadingService.start(ApiLoadingKeys.generateKeyDetectionReport);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<KeyDetectionReportRecord[]>>(
					`${this.env.apiUrl}/api/v1/report/keyDetectionReport`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.generateKeyDetectionReport);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.generateKeyDetectionReport);
		return resp!;
	}

	async generateKeyDetectionReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generateKeyDetectionReportCsv);
		let params = this.generateHttpParams(queryParams);
		params = params.set('language', this.localeService.getLocale());
		this.httpClient
			.get(`${this.env.apiUrl}/api/v1/report/keyDetectionReport/csv`, {
				responseType: 'text',
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(
						ApiLoadingKeys.generateKeyDetectionReportCsv
					);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				const blob = new Blob([response], { type: 'text/csv' });
				const url = window.URL.createObjectURL(blob);
				const fileLink = document.createElement('a');
				this.loadingService.stop(ApiLoadingKeys.generateKeyDetectionReportCsv);
				fileLink.href = url;
				fileLink.download = fileName;
				fileLink.click();
			});
	}

	async generateInventoryReport(
		queryParams?: QueryParam[]
	): Promise<InventoryReportRecord[]> {
		this.loadingService.start(ApiLoadingKeys.generateInventoryReport);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<InventoryReportRecord[]>>(
					`${this.env.apiUrl}/api/v1/report/inventoryReport`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.generateInventoryReport);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.generateInventoryReport);
		return resp!;
	}

	async getInventoryReportFilterOptions(): Promise<InventoryReportFilterOptions> {
		this.loadingService.start(ApiLoadingKeys.getInventoryReportFilterOptions);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<InventoryReportFilterOptions>>(
					`${this.env.apiUrl}/api/v1/report/inventoryReport/filterOptions`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getInventoryReportFilterOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getInventoryReportFilterOptions);
		return resp!;
	}

	async generateInventoryReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generateInventoryReportCsv);
		let params = this.generateHttpParams(queryParams);
		params = params.set('language', this.localeService.getLocale());

		this.httpClient
			.get(`${this.env.apiUrl}/api/v1/report/inventoryReport/csv`, {
				responseType: 'text',
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.generateInventoryReportCsv);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				const blob = new Blob([response], { type: 'text/csv' });
				const url = window.URL.createObjectURL(blob);
				const fileLink = document.createElement('a');
				this.loadingService.stop(ApiLoadingKeys.generateInventoryReportCsv);
				fileLink.href = url;
				fileLink.download = fileName;
				fileLink.click();
			});
	}

	async getPropertyReportFilterOptions(): Promise<PropertyReportFilterOptions> {
		this.loadingService.start(ApiLoadingKeys.getPropertyReportFilterOptions);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<PropertyReportFilterOptions>>(
					`${this.env.apiUrl}/api/v1/report/propertyReport/filterOptions`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getPropertyReportFilterOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getPropertyReportFilterOptions);
		return resp!;
	}

	async generatePropertyReport(
		queryParams?: QueryParam[]
	): Promise<PropertyReportRecord[]> {
		this.loadingService.start(ApiLoadingKeys.generateAccessReport);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<PropertyReportRecord[]>>(
					`${this.env.apiUrl}/api/v1/report/propertyReport`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.generateAccessReport);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.generateAccessReport);
		return resp!;
	}

	async generatePropertyReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generatePropertyReportCsv);
		const params = this.generateHttpParams(queryParams);
		this.httpClient
			.get(`${this.env.apiUrl}/api/v1/report/propertyReport/csv`, {
				responseType: 'text',
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.generatePropertyReportCsv);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				const blob = new Blob([response], { type: 'text/csv' });
				const url = window.URL.createObjectURL(blob);
				const fileLink = document.createElement('a');
				this.loadingService.stop(ApiLoadingKeys.generatePropertyReportCsv);
				fileLink.href = url;
				fileLink.download = fileName;
				fileLink.click();
			});
	}

	async getDeviceMapData(
		neLat?: number,
		neLng?: number,
		swLat?: number,
		swLng?: number
	): Promise<MapData[]> {
		this.loadingService.start(ApiLoadingKeys.deviceMapHome);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<MapData[]>>(
					`${this.env.apiUrl}/api/v1/dashboard/mapData?neLat=${neLat}&neLng=${neLng}&swLat=${swLat}&swLng=${swLng}`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.deviceMapHome);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.deviceMapHome);
		return resp!;
	}

	async getDashboardSettings(): Promise<DashboardSettings> {
		this.loadingService.start(ApiLoadingKeys.getDashboardSettings);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<DashboardSettings>>(
					`${this.env.apiUrl}/api/v1/dashboard/dashboardSettings`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDashboardSettings);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDashboardSettings);
		return resp!;
	}

	async getDeviceAlerts(queryParams?: QueryParam[]): Promise<DeviceAlert[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceAlerts);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<DeviceAlert[]>>(
					`${this.env.apiUrl}/api/v1/deviceAlert`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceAlerts);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceAlerts);
		return resp!;
	}

	async getTotalDeviceAlertsType(): Promise<DeviceAlertTotal[]> {
		this.loadingService.start(ApiLoadingKeys.getTotalDeviceAlertsType);
		const params = this.generateHttpParams();
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<DeviceAlertTotal[]>>(
					`${this.env.apiUrl}/api/v1/deviceAlertTotal`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getTotalDeviceAlertsType);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getTotalDeviceAlertsType);
		return resp!;
	}

	async dismissAlert(
		deviceAlertId: string,
		deviceAlert: DeviceAlert
	): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.dismissAlert);
		const body = { deviceAlert };
		const resp = await lastValueFrom(
			this.httpClient
				.put<DataResponse<boolean>>(
					`${this.env.apiUrl}/api/v1/deviceAlert/${deviceAlertId}/hidden`,
					body
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.dismissAlert);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.dismissAlert);
		return resp!;
	}

	async dismissNote(deviceNote: DeviceNote): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.dismissNote);
		const resp = await lastValueFrom(
			this.httpClient
				.put<DataResponse<boolean>>(
					`${this.env.apiUrl}/api/v1/deviceNote/hidden`,
					deviceNote
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.dismissNote);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.dismissNote);
		return resp!;
	}

	async addNote(
		deviceId?: string,
		appVisitNote?: AppVisitNote,
		appVisitPhotos?: {
			file: File;
			previewURL: string | ArrayBuffer | null;
			caption: string;
		}[]
	): Promise<string> {
		this.loadingService.start(ApiLoadingKeys.addNote);
		const formData = new FormData();
		if (appVisitPhotos) {
			for (let i = 0; i < appVisitPhotos.length; i++) {
				formData.append('file', appVisitPhotos[i].file);
				formData.append('caption', appVisitPhotos[i].caption);
			}
		}
		if (appVisitNote?.deviceId) {
			formData.append('deviceId', appVisitNote.deviceId);
		}
		formData.append('note', appVisitNote?.note ?? '');

		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<string>>(
					`${this.env.apiUrl}/api/v1/device/${deviceId}/deviceNote`,
					formData
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.addNote);
						return ApiService.handleError(err);
					})
				)
		);

		this.loadingService.stop(ApiLoadingKeys.addNote);
		return resp!;
	}

	async addDevice(serialNumberList: SerialNumber[]): Promise<SerialNumber[]> {
		this.loadingService.start(ApiLoadingKeys.addDevice);
		const resp = await lastValueFrom(
			this.httpClient
				.post<DataResponse<SerialNumber[]>>(
					`${this.env.apiUrl}/api/v1/addDevice`,
					serialNumberList
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.addDevice);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.addDevice);
		return resp!;
	}

	async getDeviceProvisionError(): Promise<Device[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceProvisionError);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<Device[]>>(
					`${this.env.apiUrl}/api/v1/getDeviceProvisionError`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceProvisionError);
						return ApiService.handleError(err);
					})
				)
		);

		this.loadingService.stop(ApiLoadingKeys.getDeviceProvisionError);
		return resp!;
	}

	async getFeatureFlagByName(featureFlagName: string): Promise<FeatureFlag> {
		this.loadingService.start(ApiLoadingKeys.getFeatureFlagByName);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<FeatureFlag>>(
					`${this.env.apiUrl}/api/v1/featureFlag/${featureFlagName}`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getFeatureFlagByName);
						return ApiService.handleError(err);
					})
				)
		);

		this.loadingService.stop(ApiLoadingKeys.getFeatureFlagByName);
		return resp!;
	}

	async getCompanySettingByName(
		companySettingName: string
	): Promise<CompanySetting> {
		this.loadingService.start(ApiLoadingKeys.getCompanySettingByName);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<CompanySetting>>(
					`${this.env.apiUrl}/api/v1/companySetting/${companySettingName}`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getCompanySettingByName);
						return ApiService.handleError(err);
					})
				)
		);

		this.loadingService.stop(ApiLoadingKeys.getCompanySettingByName);
		return resp!;
	}

	async allowCodeGeneration(
		deviceId: string,
		queryParams?: QueryParam[]
	): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.allowCodeGeneration);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<{
					data: { allowCodeGeneration: boolean };
				}>(`${this.env.apiUrl}/api/v1/device/${deviceId}/allowCodeGeneration`, {
					params,
				})
				.pipe(
					map((it) => it.data.allowCodeGeneration),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.allowCodeGeneration);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.allowCodeGeneration);
		return resp!;
	}

	async generateUsageReport(
		queryParams?: QueryParam[]
	): Promise<UsageReport[]> {
		this.loadingService.start(ApiLoadingKeys.generateUsageReport);
		const params = this.generateHttpParams(queryParams);

		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<UsageReport[]>>(
					`${this.env.apiUrl}/api/v1/report/usageReport`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.generateUsageReport);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.generateUsageReport);
		return resp!;
	}

	async generateUsageReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generateUsageReportCsv);
		const params = this.generateHttpParams(queryParams);
		this.httpClient
			.get(`${this.env.apiUrl}/api/v1/report/usageReport/csv`, {
				responseType: 'text',
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.generateUsageReportCsv);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				const blob = new Blob([response], { type: 'text/csv' });
				const url = window.URL.createObjectURL(blob);
				const fileLink = document.createElement('a');
				this.loadingService.stop(ApiLoadingKeys.generateUsageReportCsv);
				fileLink.href = url;
				fileLink.download = fileName;
				fileLink.click();
			});
	}

	async getAlertReportFilterOptions(): Promise<AlertReportFilterOptions> {
		this.loadingService.start(ApiLoadingKeys.getAlertReportFilterOptions);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<AlertReportFilterOptions>>(
					`${this.env.apiUrl}/api/v1/report/alertReport/filterOptions`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getAlertReportFilterOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAlertReportFilterOptions);
		return resp!;
	}

	async generateAlertReport(
		queryParams?: QueryParam[]
	): Promise<AlertReport[]> {
		this.loadingService.start(ApiLoadingKeys.generateAlertReport);
		const params = this.generateHttpParams(queryParams);

		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<AlertReport[]>>(
					`${this.env.apiUrl}/api/v1/report/alertReport`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.generateAlertReport);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.generateAlertReport);
		return resp!;
	}

	async generateAlertReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generateAlertReportCsv);
		let params = this.generateHttpParams(queryParams);
		params = params.set('language', this.localeService.getLocale());
		this.httpClient
			.get(`${this.env.apiUrl}/api/v1/report/alertReport/csv`, {
				responseType: 'text',
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.generateAlertReportCsv);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				const blob = new Blob([response], { type: 'text/csv' });
				const url = window.URL.createObjectURL(blob);
				const fileLink = document.createElement('a');
				this.loadingService.stop(ApiLoadingKeys.generateAlertReportCsv);
				fileLink.href = url;
				fileLink.download = fileName;
				fileLink.click();
			});
	}

	async generateAuditReport(
		queryParams?: QueryParam[]
	): Promise<AuditReportRecord[]> {
		this.loadingService.start(ApiLoadingKeys.generateAuditReport);
		const params = this.generateHttpParams(queryParams);

		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<AuditReportRecord[]>>(
					`${this.env.apiUrl}/api/v1/audit/report`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.generateAuditReport);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.generateAuditReport);
		return resp!;
	}

	async generateAuditReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generateAuditReportCsv);
		let params = this.generateHttpParams(queryParams);
		params = params.set('language', this.localeService.getLocale());
		this.httpClient
			.get(`${this.env.apiUrl}/api/v1/audit/report/csv`, {
				responseType: 'text',
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.generateAuditReportCsv);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				const blob = new Blob([response], { type: 'text/csv' });
				const url = window.URL.createObjectURL(blob);
				const fileLink = document.createElement('a');
				this.loadingService.stop(ApiLoadingKeys.generateAuditReportCsv);
				fileLink.href = url;
				fileLink.download = fileName;
				fileLink.click();
			});
	}

	async getAuditReportFilterOptions(): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.getAuditReportFilterOptions);
		let params = new HttpParams();
		params = params.set('language', this.localeService.getLocale());
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<any>>(
					`${this.env.apiUrl}/api/v1/audit/report/filterOptions`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getAuditReportFilterOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAuditReportFilterOptions);
		return resp!;
	}

	async getAuditReportDetailsOptions(queryParams?: QueryParam[]): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.getAuditReportDetailsOptions);
		let params = this.generateHttpParams(queryParams);
		params = params.set('language', this.localeService.getLocale());
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<any>>(
					`${this.env.apiUrl}/api/v1/audit/report/detailsOptions`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getAuditReportDetailsOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAuditReportDetailsOptions);
		return resp!;
	}

	async getAuditReportCompanyOptions(queryParams?: QueryParam[]): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.getAuditReportCompanyOptions);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<any>>(
					`${this.env.apiUrl}/api/v1/audit/report/companyOptions`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getAuditReportCompanyOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAuditReportCompanyOptions);
		return resp!;
	}

	async getAuditReportUserOptions(queryParams?: QueryParam[]): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.getAuditReportUserOptions);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<any>>(
					`${this.env.apiUrl}/api/v1/audit/report/userOptions`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getAuditReportUserOptions);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAuditReportUserOptions);
		return resp!;
	}

	async getAuditReportDeviceOptions(queryParams?: QueryParam[]): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.getAuditReportDeviceOptions);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<any>>(
					`${this.env.apiUrl}/api/v1/audit/report/deviceOptions`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getAuditReportDeviceOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAuditReportDeviceOptions);
		return resp!;
	}

	async getAuditReportDeviceGroupOptions(
		queryParams?: QueryParam[]
	): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.getAuditReportDeviceGroupOptions);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<any>>(
					`${this.env.apiUrl}/api/v1/audit/report/deviceGroupOptions`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getAuditReportDeviceGroupOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAuditReportDeviceGroupOptions);
		return resp!;
	}

	async getAuditReportPropertyOptions(
		queryParams?: QueryParam[]
	): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.getAuditReportPropertyOptions);
		const params = this.generateHttpParams(queryParams);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<any>>(
					`${this.env.apiUrl}/api/v1/audit/report/propertyOptions`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(
							ApiLoadingKeys.getAuditReportPropertyOptions
						);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getAuditReportPropertyOptions);
		return resp!;
	}

	async getLearningManual(queryParams: QueryParam[]): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.getLearningDoc);
		const params = this.generateHttpParams(queryParams);

		// Open the PDF as a top-level navigation to a GCS signed URL.
		// An iframe of a blob URL is refused in Safari: the web app sends
		// Content-Security-Policy: frame-ancestors 'none', and Safari's PDF
		// viewer frames that blob.
		this.httpClient
			.get<{ signedUrl: string }>(`${this.env.apiUrl}/api/v1/learning/manual`, {
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.getLearningDoc);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				this.loadingService.stop(ApiLoadingKeys.getLearningDoc);
				const signedUrl = response?.signedUrl;
				if (!signedUrl) {
					return;
				}
				window.open(signedUrl, '_blank', 'noopener,noreferrer');
			});
	}

	async getLearningVideo(queryParams: QueryParam[]): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.getLearningDoc);
		const params = this.generateHttpParams(queryParams);

		this.httpClient
			.get<{ signedUrl: string }>(`${this.env.apiUrl}/api/v1/learning/video`, {
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.getLearningDoc);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				this.loadingService.stop(ApiLoadingKeys.getLearningDoc);
				const signedUrl = response?.signedUrl;
				if (!signedUrl) {
					return;
				}
				const title = queryParams[0]?.value ?? 'Learning Video';
				const newWindow = window.open('', '_blank');
				if (newWindow) {
					newWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>${title}</title>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <link rel="icon" type="image/svg+xml" href="${window.location.origin}/assets/accessplus-favicon.svg">
                <link rel="alternate icon" href="${window.location.origin}/assets/accessplus-favicon.svg">
                <link rel="shortcut icon" href="${window.location.origin}/assets/accessplus-favicon.svg">
                <link rel="apple-touch-icon" href="${window.location.origin}/assets/accessplus-favicon.svg">
              </head>
              <body style="margin:0; padding:0; background-color: #000;">
                <video controls autoplay muted playsinline style="width:100%; height:100vh; object-fit: contain;">
                  <source src="${signedUrl}" type="video/mp4">
                </video>
              </body>
            </html>
            `);
					newWindow.document.close();
				}
			});
	}

	/** Opens a learning JPG/image via the manual endpoint (GCS manuals/ prefix). */
	async getLearningImage(queryParams: QueryParam[]): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.getLearningDoc);
		const params = this.generateHttpParams(queryParams);

		this.httpClient
			.get(`${this.env.apiUrl}/api/v1/learning/manual`, {
				responseType: 'blob',
				params,
			})
			.pipe(
				catchError((err) => {
					this.loadingService.stop(ApiLoadingKeys.getLearningDoc);
					return ApiService.handleError(err);
				})
			)
			.subscribe((response) => {
				const blob = new Blob([response], { type: 'image/jpeg' });
				this.loadingService.stop(ApiLoadingKeys.getLearningDoc);
				const url = window.URL.createObjectURL(blob);
				const newWindow = window.open('', '_blank');
				if (newWindow) {
					newWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>${queryParams[0].value}</title>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <link rel="icon" type="image/svg+xml" href="${window.location.origin}/assets/accessplus-favicon.svg">
                <link rel="alternate icon" href="${window.location.origin}/assets/accessplus-favicon.svg">
                <link rel="shortcut icon" href="${window.location.origin}/assets/accessplus-favicon.svg">
                <link rel="apple-touch-icon" href="${window.location.origin}/assets/accessplus-favicon.svg">
              </head>
              <body style="margin:0; padding:0; background-color: #fff; display:flex; justify-content:center; align-items:center; min-height:100vh;">
                <img src="${url}" alt="${queryParams[0].value}" style="max-width:100%; max-height:100vh; object-fit: contain;" />
                <script>
                  window.addEventListener('beforeunload', function() {
                    URL.revokeObjectURL('${url}');
                  });
                  document.close();
                </script>
              </body>
            </html>
          `);
					newWindow.document.close();
				}
			});
	}

	async getDeviceDeviceSettingsKV(
		deviceId: string
	): Promise<DeviceSettingsKV[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceDeviceSettingsKV);
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<DeviceSettingsKV[]>>(
					`${this.env.apiUrl}/api/v1/device/${deviceId}/getDeviceSettingsKV`
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop(ApiLoadingKeys.getDeviceDeviceSettingsKV);
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.getDeviceDeviceSettingsKV);
		return resp!;
	}

	async disableAjaxForCompany(companyId: string): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.disableAjaxForCompany);
		await lastValueFrom(
			this.httpClient
				.put(`${this.env.apiUrl}/api/v1/company/${companyId}/disableAJAX`, {})
				.pipe(
					catchError((error) => {
						this.loadingService.stop(ApiLoadingKeys.disableAjaxForCompany);
						return ApiService.handleError(error);
					})
				)
		);
		this.loadingService.stop(ApiLoadingKeys.disableAjaxForCompany);
	}

	async getDeviceControlDescendantCompanies(ids: string[]): Promise<any[]> {
		this.loadingService.start('getDeviceControlDescendantCompanies');
		let params = new HttpParams();
		ids.forEach((id) => {
			params = params.append('controlAssignmentIds', id);
		});
		const resp = await lastValueFrom(
			this.httpClient
				.get<DataResponse<any[]>>(
					`${this.env.apiUrl}/api/v1/deviceControl/descendantCompanies`,
					{ params }
				)
				.pipe(
					map((it) => it.data),
					catchError((err) => {
						this.loadingService.stop('getDeviceControlDescendantCompanies');
						return ApiService.handleError(err);
					})
				)
		);
		this.loadingService.stop('getDeviceControlDescendantCompanies');
		return resp!;
	}
}

export interface APIError {
	errorStatusCode: number;
	errors: [
		{
			statusCode: number;
			title: string;
			detail: string;
		},
	];
}

export interface QueryParam {
	param: string;
	value: string | number | boolean | string[] | number[];
}

export interface PaginationLinks {
	first: string;
	previous: string;
	next: string;
	last: string;
}

export interface PasswordResetTokenStatus {
	status: string;
}

export enum DeviceRemovalActions {
	perform = 'perform',
	request = 'request',
	cancel = 'cancel',
	requestEndAllAccesses = 'requestEndAllAccesses',
	requestTransferAccesses = 'requestTransferAccesses',
}

export enum ApiLoadingKeys {
	login = 'LOADING_INDICATOR_KEY_APISERVICES_login',
	requestMfaToken = 'LOADING_INDICATOR_KEY_APISERVICES_requestMfaToken',
	verifyMfaToken = 'LOADING_INDICATOR_KEY_APISERVICES_verifyMfaToken',
	updateCustomClaims = 'LOADING_INDICATOR_KEY_APISERVICES_updateCustomClaims',
	getSentrilockCompanyId = 'LOADING_INDICATOR_KEY_APISERVICES_getSentrilockCompanyId',
	sendPasswordResetEmail = 'LOADING_INDICATOR_KEY_APISERVICES_sendPasswordResetEmail',
	checkPasswordResetTokenStatus = 'LOADING_INDICATOR_KEY_APISERVICES_checkPasswordResetTokenStatus',
	resetPassword = 'LOADING_INDICATOR_KEY_APISERVICES_resetPassword',
	changePassword = 'LOADING_INDICATOR_KEY_APISERVICES_changePassword',
	getUserProfile = 'LOADING_INDICATOR_KEY_APISERVICES_getUserProfile',
	updateUser = 'LOADING_INDICATOR_KEY_APISERVICES_updateUser',
	getUserNotificationPreferences = 'LOADING_INDICATOR_KEY_APISERVICES_getUserNotificationPreferences',
	updateUserNotificationPreferences = 'LOADING_INDICATOR_KEY_APISERVICES_updateUserNotificationPreferences',
	updateUserProfile = 'LOADING_INDICATOR_KEY_APISERVICES_updateUserProfile',
	validateEmail = 'LOADING_INDICATOR_KEY_APISERVICES_validateEmail',
	requestEmailVerificationCode = 'LOADING_INDICATOR_KEY_APISERVICES_requestEmailVerificationCode',
	verifyEmailVerificationCode = 'LOADING_INDICATOR_KEY_APISERVICES_verifyEmailVerificationCode',
	getUserDetails = 'LOADING_INDICATOR_KEY_APISERVICES_getUserDetails',
	getUserAndRole = 'LOADING_INDICATOR_KEY_APISERVICES_getUserAndRole',
	getUserEventHistory = 'LOADING_INDICATOR_KEY_APISERVICES_getUserEventHistory',
	getAllUsers = 'LOADING_INDICATOR_KEY_APISERVICES_getAllUsers',
	searchCompaniesPublicDirectory = 'LOADING_INDICATOR_KEY_APISERVICES_searchCompaniesPublicDirectory',
	getCompanyById = 'LOADING_INDICATOR_KEY_APISERVICES_getCompanyById',
	getCompanyByInfo = 'LOADING_INDICATOR_KEY_APISERVICES_getCompanyByInfo',
	editCompanyById = 'LOADING_INDICATOR_KEY_APISERVICES_editCompanyById',
	updateCompanyRelationship = 'LOADING_INDICATOR_KEY_APISERVICES_updateCompanyRelationship',
	getCompanyRelationships = 'LOADING_INDICATOR_KEY_APISERVICES_getCompanyRelationships',
	getCompanyFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getCompanyFilterOptions',
	getPostalCodeFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getPostalCodeFilterOptions',
	getCompanyRelationshipsByCompanyId = 'LOADING_INDICATOR_KEY_APISERVICES_getCompanyRelationshipsByCompanyId',
	getInstallationLocationNameFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getInstallationLocationNameFilterOptions',
	createCompanyRelationship = 'LOADING_INDICATOR_KEY_APISERVICES_createCompanyRelationship',
	createNewCompany = 'LOADING_INDICATOR_KEY_APISERVICES_createNewCompany',
	getCompanyRolesByCompanyId = 'LOADING_INDICATOR_KEY_APISERVICES_getCompanyRolesByCompanyId',
	createDeviceGroup = 'LOADING_INDICATOR_KEY_APISERVICES_createDeviceGroup',
	updateDeviceGroupMembership = 'LOADING_INDICATOR_KEY_APISERVICES_updateDeviceGroupMembership',
	updateDeviceGroupName = 'LOADING_INDICATOR_KEY_APISERVICES_updateDeviceGroupName',
	getSystemCapabilitiesByRoleId = 'LOADING_INDICATOR_KEY_APISERVICES_getSystemCapabilitiesByRoleId',
	getRoleCapabilities = 'LOADING_INDICATOR_KEY_APISERVICES_getRoleCapabilities',
	getMyRoles = 'LOADING_INDICATOR_KEY_APISERVICES_getMyRoles',
	getRoleById = 'LOADING_INDICATOR_KEY_APISERVICES_getRoleById',
	updateRole = 'LOADING_INDICATOR_KEY_APISERVICES_updateRole',
	getAssignedRoleUsers = 'LOADING_INDICATOR_KEY_APISERVICES_getAssignedRoleUsers',
	createNewRole = 'LOADING_INDICATOR_KEY_APISERVICES_createNewRole',
	assignRoleToUserId = 'LOADING_INDICATOR_KEY_APISERVICES_assignRoleToUserId',
	unassignRoleFromUserId = 'LOADING_INDICATOR_KEY_APISERVICES_unassignRoleFromUserId',
	getCompanyUsersByCompanyId = 'LOADING_INDICATOR_KEY_APISERVICES_getCompanyUsersByCompanyId',
	getMyUsers = 'LOADING_INDICATOR_KEY_APISERVICES_getMyUsers',
	getCompanyPropertiesByCompanyId = 'LOADING_INDICATOR_KEY_APISERVICES_getCompanyPropertiesByCompanyId',
	getTimezone = 'LOADING_INDICATOR_KEY_APISERVICES_getTimezone',
	getCountryAvailableSms = 'LOADING_INDICATOR_KEY_APISERVICES_getCountryAvailableSms',
	getContracts = 'LOADING_INDICATOR_KEY_APISERVICES_getContracts',
	getSingleContract = 'LOADING_INDICATOR_KEY_APISERVICES_getSingleContract',
	saveContract = 'LOADING_INDICATOR_KEY_APISERVICES_saveContract',
	updateContract = 'LOADING_INDICATOR_KEY_APISERVICES_updateContract',
	createNewProperty = 'LOADING_INDICATOR_KEY_APISERVICES_createNewProperty',
	getProperties = 'LOADING_INDICATOR_KEY_APISERVICES_getProperties',
	getClients = 'LOADING_INDICATOR_KEY_APISERVICES_getClients',
	getClientsByLocationId = 'LOADING_INDICATOR_KEY_APISERVICES_getClientsByLocationId',
	createNewClient = 'LOADING_INDICATOR_KEY_APISERVICES_createNewClient',
	createClientWithNewProperty = 'LOADING_INDICATOR_KEY_APISERVICE_createClientWithNewProperty',
	getClientDetails = 'LOADING_INDICATOR_KEY_APISERVICES_getClientDetails',
	getClientProperty = 'LOADING_INDICATOR_KEY_APISERVICES_getClientProperty',
	getClientDevices = 'LOADING_INDICATOR_KEY_APISERVICES_getClientDevices',
	updateClientDetails = 'LOADING_INDICATOR_KEY_APISERVICE_updateClientDetails',
	getContacts = 'LOADING_INDICATOR_KEY_APISERVICES_getContacts',
	addEditContacts = 'LOADING_INDICATOR_KEY_APISERVICES_addEditContacts',
	deleteContact = 'LOADING_INDICATOR_KEY_APISERVICES_deleteContact',
	getProperty = 'LOADING_INDICATOR_KEY_APISERVICES_getProperty',
	getPropertyEventHistory = 'LOADING_INDICATOR_KEY_APISERVICES_getPropertyEventHistory',
	editPropertyById = 'LOADING_INDICATOR_KEY_APISERVICES_editPropertyById',
	editDeviceById = 'LOADING_INDICATOR_KEY_APISERVICES_editDeviceById',
	getAllDevices = 'LOADING_INDICATOR_KEY_APISERVICES_getAllDevices',
	getDevicesByCompanyId = 'LOADING_INDICATOR_KEY_APISERVICES_getDevicesByCompanyId',
	getDevicesByPropertyId = 'LOADING_INDICATOR_KEY_APISERVICES_getDevicesByPropertyId',
	getDevicesByDeviceGroupId = 'LOADING_INDICATOR_KEY_APISERVICES_getDevicesByDeviceGroupId',
	getAvailableDevices = 'LOADING_INDICATOR_KEY_APISERVICES_getAvailableDevices',
	getDeviceById = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceById',
	getWasDeviceOwner = 'LOADING_INDICATOR_KEY_APISERVICES_getWasDeviceOwner',
	getDeviceBySerialNumber = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceBySerialNumber',
	getDeviceInstallationPhotos = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceInstallationPhotos',
	saveDeviceInstallationPhoto = 'LOADING_INDICATOR_KEY_APISERVICES_saveDeviceInstallationPhoto',
	getAppNotes = 'LOADING_INDICATOR_KEY_APISERVICES_getAppNotes',
	getAllDeviceNotes = 'LOADING_INDICATOR_KEY_APISERVICES_getAllDeviceNotes',
	addNote = 'LOADING_INDICATOR_KEY_APISERVICES_addNote',
	addDevice = 'LOADING_INDICATOR_KEY_APISERVICES_addDevice',
	getDeviceProvisionError = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceProvisionError',
	getDeviceGroups = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceGroups',
	getDeviceGroupsByCompanyId = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceGroupsByCompanyId',
	getDeviceGroupOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceGroupOptions',
	getDeviceGroupById = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceGroupById',
	getInheritedDeviceGroupControlById = 'LOADING_INDICATOR_KEY_APISERVICES_getInheritedDeviceGroupControlById',
	getDeviceControls = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceControls',
	transferDeviceOwnership = 'LOADING_INDICATOR_KEY_APISERVICES_transferDeviceOwnership',
	getControlAssignments = 'LOADING_INDICATOR_KEY_APISERVICES_getControlAssignments',
	deleteControlAssignments = 'LOADING_INDICATOR_KEY_APISERVICES_deleteControlAssignments',
	editControlAssignments = 'LOADING_INDICATOR_KEY_APISERVICES_editControlAssignments',
	getControlAssignmentOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getControlAssignmentOptions',
	createDeviceControlsAndControlAssignments = 'LOADING_INDICATOR_KEY_APISERVICES_createDeviceControlsAndControlAssignments',
	getOneDayCode = 'LOADING_INDICATOR_KEY_APISERVICES_getOneDayCode',
	getAccessCode = 'LOADING_INDICATOR_KEY_APISERVICES_getAccessCode',
	requestAndSaveOneDayCode = 'LOADING_INDICATOR_KEY_APISERVICES_requestAndSaveOneDayCode',
	getFlexCode = 'LOADING_INDICATOR_KEY_APISERVICES_getFlexCode',
	requestAndSaveFlexCode = 'LOADING_INDICATOR_KEY_APISERVICES_requestAndSaveFlexCode',
	requestAndSaveHaspCode = 'LOADING_INDICATOR_KEY_APISERVICES_requestAndSaveHaspCode',
	requestDeviceRemovalAction = 'LOADING_INDICATOR_KEY_APISERVICES_requestDeviceRemovalAction',
	getDeviceEventHistory = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceEventHistory',
	getEventHistoryReport = 'LOADING_INDICATOR_KEY_APISERVICES_getEventHistoryReport',
	registerNewUser = 'LOADING_INDICATOR_KEY_APISERVICES_registerNewUser',
	checkNewUserRegistrationToken = 'LOADING_INDICATOR_KEY_APISERVICES_checkNewUserRegistrationToken',
	saveMobilePhoneNumber = 'LOADING_INDICATOR_KEY_APISERVICES_saveMobilePhoneNumber',
	saveEmail = 'LOADING_INDICATOR_KEY_APISERVICES_saveEmail',
	getSecurityQuestions = 'LOADING_INDICATOR_KEY_APISERVICES_getSecurityQuestions',
	getSecurityAnswers = 'LOADING_INDICATOR_KEY_APISERVICES_getSecurityAnswers',
	requestPhoneVerificationCode = 'LOADING_INDICATOR_KEY_APISERVICES_requestPhoneVerificationCode',
	verifyPhoneVerificationCode = 'LOADING_INDICATOR_KEY_APISERVICES_verifyPhoneVerificationCode',
	completeNewUserRegistration = 'LOADING_INDICATOR_KEY_APISERVICES_completeNewUserRegistration',
	updateDeviceSettings = 'LOADING_INDICATOR_KEY_APISERVICES_updateDeviceSettings',
	editDeviceControl = 'LOADING_INDICATOR_KEY_APISERVICES_editDeviceControl',
	getCodeGenReportFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getCodeGenReportFilterOptions',
	generateCodeGenReport = 'LOADING_INDICATOR_KEY_APISERVICES_generateCodeGenReport',
	generateCodeGenReportCsv = 'LOADING_INDICATOR_KEY_APISERVICES_generateCodeGenReportCsv',
	getAccessReportFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getAccessReportFilterOptions',
	generateAccessReport = 'LOADING_INDICATOR_KEY_APISERVICES_generateAccessReport',
	startAccessReportExport = 'LOADING_INDICATOR_KEY_APISERVICES_startAccessReportExport',
	downloadAccessReportExport = 'LOADING_INDICATOR_KEY_APISERVICES_downloadAccessReportExport',
	getKeyDetectionReportFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getKeyDetectionReportFilterOptions',
	generateKeyDetectionReport = 'LOADING_INDICATOR_KEY_APISERVICES_generateKeyDetectionReport',
	generateKeyDetectionReportCsv = 'LOADING_INDICATOR_KEY_APISERVICES_generateKeyDetectionReportCsv',
	getInventoryReportFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getInventoryReportFilterOptions',
	generateInventoryReportCsv = 'LOADING_INDICATOR_KEY_APISERVICES_generateInventoryReportCsv',
	generateInventoryReport = 'LOADING_INDICATOR_KEY_APISERVICES_generateInventoryReport',
	getPropertyReportFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getPropertyReportFilterOptions',
	getDeviceFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceFilterOptions',

	generatePropertyReport = 'LOADING_INDICATOR_KEY_APISERVICES_generatePropertyReport',
	generatePropertyReportCsv = 'LOADING_INDICATOR_KEY_APISERVICES_generatePropertyReportCsv',
	resendUserRegistration = 'LOADING_INDICATOR_KEY_APISERVICES_resendUserRegistration',
	deviceMapHome = 'LOADING_INDICATOR_KEY_APISERVICES_deviceMapHome',
	getDeviceAlerts = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceAlerts',
	getDashboardSettings = 'LOADING_INDICATOR_KEY_APISERVICES_getDashboardSettings',
	getTotalDeviceAlertsType = 'LOADING_INDICATOR_KEY_APISERVICES_getTotalDeviceAlertsType',
	dismissAlert = 'LOADING_INDICATOR_KEY_APISERVICES_dismissAlert',
	dismissNote = 'LOADING_INDICATOR_KEY_APISERVICES_dismissNote',
	getFeatureFlagByName = 'LOADING_INDICATOR_KEY_APISERVICES_getFeatureFlagByName',
	getCompanySettingByName = 'LOADING_INDICATOR_KEY_APISERVICES_getCompanySettingByName',
	allowCodeGeneration = 'LOADING_INDICATOR_KEY_APISERVICES_allowCodeGeneration',
	generateUsageReport = 'LOADING_INDICATOR_KEY_APISERVICES_generateUsageReport',
	generateUsageReportCsv = 'LOADING_INDICATOR_KEY_APISERVICES_generateUsageReportCsv',
	getAlertReportFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getAlertReportFilterOptions',
	generateAlertReport = 'LOADING_INDICATOR_KEY_APISERVICES_generateAlertReport',
	generateAlertReportCsv = 'LOADING_INDICATOR_KEY_APISERVICES_generateAlertReportCsv',
	generateAuditReport = 'LOADING_INDICATOR_KEY_APISERVICES_generateAuditReport',
	generateAuditReportCsv = 'LOADING_INDICATOR_KEY_APISERVICES_generateAuditReportCsv',
	getAuditReportFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getAuditReportFilterOptions',
	getAuditReportDetailsOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getAuditReportDetailsOptions',
	getAuditReportCompanyOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getAuditReportCompanyOptions',
	getAuditReportUserOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getAuditReportUserOptions',
	getAuditReportDeviceOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getAuditReportDeviceOptions',
	getAuditReportDeviceGroupOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getAuditReportDeviceGroupOptions',
	getAuditReportPropertyOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getAuditReportPropertyOptions',
	getLearningDoc = 'LOADING_INDICATOR_KEY_APISERVICES_getLearningDoc',
	getWhitelistUserListForCompany = 'LOADING_INDICATOR_KEY_APISERVICES_getWhitelistUserListForCompany',
	updateOrAddNoteFromWhitelist = 'LOADING_INDICATOR_KEY_APISERVICES_updateOrAddNoteFromWhitelist',
	updateWhitelistStatus = 'LOADING_INDICATOR_KEY_APISERVICES_updateWhitelistStatus',
	getDeviceDeviceSettingsKV = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceDeviceSettingsKV',
	getDeviceGroupFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getPostalCodeFilterOptions',
	disableAjaxForCompany = 'LOADING_INDICATOR_KEY_APISERVICES_disableAjaxForCompany',
	getDeviceControlDescendantCompanies = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceControlDescendantCompanies',
	getCompanyOwnsDevices = 'LOADING_INDICATOR_KEY_APISERVICES_getCompanyOwnsDevices',
	getVerificationOfTaxIdBasedOnValue = 'LOADING_INDICATOR_KEY_APISERVICES_getVerificationOfTaxIdBasedOnValue',
}
